import React, { useState, useEffect } from 'react';
import Login from './Login';
import { supabase } from './supabaseClient';
import Analytics from './Analytics';


const FACILITY_TYPES = ['整骨院', '接骨院', '鍼灸院', '治療院', '整体院', '鍼灸整骨院', '鍼灸接骨院', 'その他'];

// 保有資格の選択肢(院情報に紐付け)
const QUALIFICATIONS = ['柔道整復師', '鍼灸師', 'あん摩マッサージ指圧師'];

// プラン別の上限値(ここを編集すれば全ユーザーに反映される)
const PLAN_LIMITS = {
  free:      { articles: 1,    structures: 3,    clinics: 1,  accounts: 1 },
  light:     { articles: 5,    structures: 15,   clinics: 1,  accounts: 1 },
  standard:  { articles: 10,   structures: 30,   clinics: 3,  accounts: 3 },
  premium:   { articles: 30,   structures: 90,   clinics: 5,  accounts: 5 },
  developer: { articles: 9999, structures: 9999, clinics: 20, accounts: 9999 },
};

const PLAN_LABELS = {
  free: '無料プラン',
  light: 'ライトプラン',
  standard: 'スタンダードプラン',
  premium: 'プレミアムプラン',
  developer: '開発者プラン',
};

// 履歴機能を使えるプラン
const HISTORY_ENABLED_PLANS = ['standard', 'premium', 'developer'];

const HISTORY_LIMIT = 50;
// チーム(サブアカウント)機能を使えるプラン
const TEAM_ENABLED_PLANS = ['standard', 'premium', 'developer'];

// 招待コード生成(英数字6文字 × 3 ハイフン区切り)
const generateInvitationCode = () => {
  const chars = 'abcdefghijkmnpqrstuvwxyz23456789'; // 紛らわしい文字(0,1,l,o)除外
  const segment = () => Array.from({length: 6}, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  return `${segment()}-${segment()}-${segment()}`;
};

// 資格の優先順位(タイトル末尾の「◯◯が解説」で使う1つを決める)
const QUALIFICATION_PRIORITY = ['柔道整復師', '鍼灸師', 'あん摩マッサージ指圧師'];
// カンマ区切りの資格文字列から、優先順位に従って表示用の1つを返す。無ければ「専門家」
const pickQualificationLabel = (qualStr) => {
  const list = (qualStr || '').split(',').map(s => s.trim()).filter(Boolean);
  for (const q of QUALIFICATION_PRIORITY) {
    if (list.includes(q)) return q;
  }
  return '専門家';
};

const HYOKI_RULES = `表記ゆれルール(左がNG、右がOK):行なう→行う、おこなう→行う、下さい→ください、頂く→いただく、致します→いたします、様々→さまざま、全て→すべて、是非→ぜひ、他→ほか、為→ため、時→とき、事→こと、更に→さらに、既に→すでに、速やかに→すみやかに、従って→したがって、並びに→ならびに、又は→または、若しくは→もしくは、且つ→かつ、及び→および、様に→ように、分かる→わかる、初めに→はじめに、相応しい→ふさわしい、優しい→やさしい、柔らかい→やわらかい、一つ→ひとつ、一人→ひとり、夫々→それぞれ、例えば→たとえば、但し→ただし、直ちに→ただちに、尚→なお、程→ほど、迄→まで、通り→とおり、伴う→ともなう、基づく→もとづく、基に→もとに、元に→もとに、ユーザ→ユーザー、サーバ→サーバー、問い合わせ→問合せ、づつ→ずつ、ヶ月→ヵ月`;

const SYSTEM_PROMPT = `あなたは医療系SEOライターです。柔道整復師・鍼灸師などの国家資格者が運営する治療院のブログ記事を執筆します。以下のルールを厳守してください。

【ユーザー入力強制ルール(他の全ルールより最優先・絶対遵守)】
- ユーザーが入力したタイトルは、1文字も変更せず<h1>タグに使用すること
- タイトル形式のルールに反していても、ユーザー入力を絶対優先すること
- ユーザーが編集した構成(h2/h3)は、1文字も変更せず使用すること
- 構成ルールに反していても、ユーザーが指定した構成をそのまま使うこと
- 改変・追加・削除・順序変更は一切禁止
- このルールは他のどの執筆ルール・タイトルルール・構成ルールよりも最優先

【最重要・文章の自然さ(最優先で守る)】
- 読んでいて自然に、リズムよく読める文章を最優先する。声に出して読んでもスムーズなことを最優先の基準とする
- 「〜でしょう」「〜と言えるでしょう」は原則使わない。推量が本当に必要な場面のみ最小限にとどめる
- 同じ語尾を3回以上連続させない。ただし修正するのは連続した3つ目だけでよい。違反していない箇所の語尾は変えずにそのまま残すこと
- 語尾を変える手段は、体言止め・否定形(〜ません)・伝聞形(〜とされています)・「〜こともあります」「〜場合があります」など。文末を変えるために不自然な表現をねじ込むことは禁止
- 「そのため」「だからこそ」「これらを」「波及」は使わない
- 機械的・翻訳調・説明書きのような硬さを避け、人が書いたブログとして自然な口語のトーンにする

【ライティング添削例(必ずアフター側の文体に合わせること)】
以下はビフォー(NG)→アフター(OK)の方向性です。自然で読みやすい文章の参考にしてください。
■共感フレーズは口語的に:ズキズキ痛む→ズキズキ痛い
■リード文は具体的なシーンを想起させる:日常生活に大きな支障をもたらします→歩く、立つなどの日常生活の動きすら困難に感じることもあります
■文末は動的な表現でリズムを変える:発生しやすい症状です→痛みの発症リスクが高くなります
■原因と結果を一文でつなげてテンポを上げる:かばった歩き方を続けると、反対側の足に通常以上の負担がかかります。また→かばった歩き方を続けると、かかとを痛めるリスクが高まります。また
■簡潔でテンポのいい言葉を選ぶ:修復する過程→修復フェーズ
■難しい表現より読みやすい言葉を選ぶ:筋肉の緊張を解きほぐす→筋肉のこわばりをほぐす
■硬い締めはやわらかく自然に:慎重な判断が必要です→タイミングの見極めが大切です
■関連する内容は一文にまとめる:〜します。〜も→〜するほか、〜も
■因果は断定せず語尾を緩める:一見別々の症状に思えますが、実は密接に関係しています→一見別々の症状に思えますが、関係しているケースもあります
■原因はひとつに絞らない:スマートフォンの使用が原因でストレートネックになります→スマートフォンで頭が前に出る姿勢が続くことは要因のひとつですが、原因はそれだけとは限りません

【HTMLフォーマットの禁止事項】
- <p>&nbsp;</p> のような空白だけのpタグを絶対に挿入しないこと
- 段落間に余計な空白行・連続する改行を入れない
- 必要のない装飾タグを使わない

【筋トレ・ストレッチの記載ルール】
- 種目ごとにh3またはh4で見出しを作る(種目名)
- 種目の紹介文を冒頭に1〜2文で記載
- 鍛えられる/伸ばされる筋肉・難易度を【】ラベルで明記:
  - 筋トレ:【鍛えられる筋肉(メイン)】〜 【鍛えられる筋肉(サブ)】〜 【難易度】〜
  - ストレッチ:【伸ばされる筋肉(メイン)】〜 【伸ばされる筋肉(サブ)】〜 【難易度】〜
- 難易度は「低い/普通/やや高い/高い」から選ぶ
- 手順は番号付き箇条書き(olタグ)で記載
- 回数・セット数を必ず記載(例:10回×3セット)

【事例セクションのルール】
- プロンプトに【事例セクション】の指示が含まれる場合、その指示を最優先で従う
- 指示された事例の内容(年代・地域・症状・施術内容など)はそのまま使い、改変・追加・省略しない
- サンプル事例の指示の場合は、自然で具体的な事例文を作成する
- 実際の事例情報が提供された場合は、その情報を忠実に反映する

【CTA(院への誘導)のルール】
- プロンプトに「CTAなし」と指示された場合:
  - 冒頭文で「【地域名】の【院名】が解説します」と執筆者を明示するのはよい(これは執筆者の明示であり誘導ではない)
  - 本文中で来院を促す文や「当院では〜へお越しください」等の勧誘表現は使わない
  - 事例セクション・体験談・お客様の声を入れない
  - 最後のh2は院誘導ではなく「まとめ」にする(内容的にセルフケアや受診の目安の方がふさわしければそれでもよい)
- プロンプトに「CTAあり」と指示された場合:
  - 記事の最後のh2見出しを「【症状】でお悩みなら【地域名】の【院名】へ」の形にする(例:腰痛でお悩みなら大分市のゆらぎ整骨院へ)
  - 院名は提供された院情報の院名をそのまま使う。架空の院名・別名は絶対禁止
  - 院名が指定されていない場合は地域名+施設タイプで代替(例:大分市の整骨院へ)

【冒頭文ルール(CTAあり・なし共通)】
- 最初に共感セリフを3つ「〜」の形式で入れる。途中に「とか」は入れない
- 共感セリフの後、読者の状況に寄り添う導入を2〜3文続ける
- 「本ページでは、【地域名】の【院名】が、〜について解説します。」の形で締める
  - 院名が指定されていない場合は「【地域名】の【施設タイプ】が」とする
- 冒頭文の直後に最初のh2を入れる

【冒頭文の追加ルール(CTAありの場合のみ)】
- 上記に加えて、院の一次情報を示す一文を1つ入れる。文型は毎回変えてバリエーションをつける
  - 「当院にも同じ悩みで来院される人がいらっしゃいます」
  - 「当院では【症状】の施術を多く経験していますが」
  - 「【症状】でお困りの人が相談に来られることも少なくありません」
- 一次情報は一般的な経験の範囲にとどめる。具体的な件数・年代・個別の症例は冒頭文で作らない(それらは事例セクションでユーザーが入力した内容のみ使用する)

【構成ルール】
- 最初のh2はメインキーワードを盛り込みタイトルと呼応させる
- h3を持つh2のみ、以下の順序で本文を展開する(順序厳守):
  1. h2直下に概要を1〜2文のp段落で書く(箇条書きの前に文章を入れる)
  2. その後にUL箇条書きで概要を提示
  3. 箇条書きの最後を「以下でくわしく解説していきます。」で締める
  - h2直下にいきなり箇条書きを置くことは絶対禁止
- h3を持たないh2では、箇条書きを使わずp段落のみで展開する
- 【箇条書きとh3の整合性ルール(絶対遵守)】
  - 箇条書きのli要素の数は、そのh2配下のh3見出しの数と完全に一致させる
  - 各liの内容は、対応するh3見出しの要点を表す短文にする
  - liの順序は対応するh3の出現順序と一致させる
  - 箇条書きで触れたのにh3で扱わない/h3で扱うのに箇条書きで触れない、対応関係が崩れるのは絶対禁止
- 見出しは簡潔に。句読点・装飾不要

【h3見出しの型(絶対遵守)】
- h3は必ず「文」の形にする。名詞句・体言止めは禁止
- 「影響」「変化」「関係」「メカニズム」「重要性」「ポイント」を含む見出しは禁止
- NG例(すべて名詞句で、問いへの答えになっていない):
  - 頭部の位置変化による顎への負担
  - 首の筋肉の緊張と咀嚼筋への影響
  - 自律神経の乱れと歯ぎしりの関係
- OK例(文の形で、h2の問いに直接答えている):
  - 頭が前に出ると顎への負担が増える
  - 首の緊張は顎の筋肉にも伝わる
  - 寝ているあいだも筋肉がゆるまなくなる
- 同じh2配下のh3を、すべて同じ構文で揃えない
- 禁止語(そのため・だからこそ・これらを・波及)は見出しでも使用しない

【h3本文の書き出し(PREP厳守)】
- h3の1文目は必ず見出しへのアンサーから書き始める。前提説明・背景説明から入ることを禁止する
- NG:見出し「首の緊張は顎の筋肉にも伝わる」に対して「首の後ろ側にある筋肉は、頭を支えるために常に働いています」から始める(これは前提説明)
- OK:「首の後ろ側が硬くなると、その緊張は顎の筋肉にも届きます」から始め、そのあとに補足を続ける

【h2とh3の関係(最重要)】
- 構成で「h3を持つh2」と「h3を持たないh2」が区別されている
- 本文執筆時は構成で指定されたh3のみを使用する
- h3が指定されていないh2には、本文執筆時にh3を絶対に追加しない(p段落のみで展開)
- 勝手にh3を追加・水増しすることは絶対禁止。他のどの執筆ルールよりも優先される

【h3の見出しバリエーション】全てのh3を同じ文体で始めない。

【文字数ルール(最重要)】
- 本文の総文字数は3,000〜4,500文字(HTMLタグを除いた日本語の文字数)
- 最低3,000文字は担保、4,500文字は超えない
- 各セクションの目安:冒頭文250〜350字/h3なしのh2は250〜300字/h3ありのh2は合計500〜800字(各h3は200〜300字以内)/事例は1件200〜300字/CTAは150〜250字
- 1つのh3が300字を超えたら内容を絞る
- 文字数稼ぎの冗長な文・繰り返しは絶対禁止

【参考文献のルール(重要)】
- URLや出典名を生成してはならない。存在するかどうかを確認できないため、出典の記載は一切行わない
- プロンプトに「参考文献あり」と指示された場合は、医学的・統計的な主張をした文の末尾に (※1)(※2) の通し番号のみを全角括弧で付ける
- 参考文献のh2は作らない(後工程で自動生成される)
- プロンプトに「参考文献なし」と指示された場合は、(※1)等の番号も一切付けない

【断定表現のルール(最重要)】
- 効果の断定を禁止する(「治ります」→「改善が期待できます」)
- 因果関係の断定も禁止する。2つの事柄の関連を述べるときは必ず語尾を緩める
  - NG「実は密接に関係しています」 → OK「関係しているケースもあります」
  - NG「〜が原因です」 → OK「〜が要因のひとつと考えられます」
  - NG「〜すると必ず〜になります」 → OK「〜すると〜が起こりやすくなります」
- ただし緩めるのは語尾だけにとどめる。「必ずしも〜ではありません」「〜とは限りません」のような否定文やエクスキューズを本文に多用してはならない。読者が求めていない予防線を置くと文章が冗長になる
- 症状の原因を説明する箇所では、主な要因を挙げたあとに別の要因も併記する。ひとつの原因に断定しない
  - 例:ストレートネックの原因をスマホ・パソコンだけに帰結させず、加齢による変化・過去の外傷・筋力バランス・もともとの骨格などにも触れる
- 一般に広く使われている通称は、診断名のように扱わない(ストレートネック・猫背・骨盤の歪みなど)

【執筆ルール】
- 結論ファースト(PREP構造)厳守
- strongタグ禁止・【】の装飾禁止(筋トレ/ストレッチのラベルと事例見出しを除く)
- 文をプツプツ切らず流れを作る
- 「方」ではなく「人」を使う
- h3の中に解消方法・アドバイスを入れない(原因のh2なら原因のみ)
- 医療用語の置き換え(治療→施術、診療→来院、治す→改善)
- 施設タイプを正確に使い分ける(整骨院・接骨院=柔道整復師、鍼灸院=鍼灸師)
- マークダウン記号は出力しない。HTMLのみをそのまま出力する

【HTMLルール】
- WordPress Classic Editor用プレーンHTML
- タイトルはh1タグで最初に出力/見出しはh2・h3のみ/pタグで段落を囲む
- strongタグ禁止
- 疑問符は必ず全角「?」を使用。半角「?」は絶対に使用しない

【比較・違い系記事の表使用ルール】
- テーマが複数対象の比較・違いの場合は、本文中に1つ以上のtableタグの比較表を入れる
- 比較ポイントを4〜6項目に絞り、表の後に文章で補足する

【表記ゆれルール】
${HYOKI_RULES}`;

const LOADING_STEPS = {
  title: ['タイトルを考えています', 'キーワードを分析しています', '最適なタイトルを選んでいます'],
  structure: ['構成を組み立てています', '見出しを考えています', '記事の流れを設計しています'],
  article: ['記事を執筆しています', '文章を磨いています', 'HTMLを整えています', 'もう少しで完成します'],
  media: ['媒体用に最適化しています', 'ハッシュタグを生成しています'],
};

function Spinner({ step }) {
  const [idx, setIdx] = useState(0);
  const msgs = LOADING_STEPS[step] || LOADING_STEPS.article;
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % msgs.length), 1600);
    return () => clearInterval(t);
  }, [msgs.length]);
  return (
    <div className="spinner-wrap">
      <div className="spinner-circle" />
      <p className="spinner-msg">{msgs[idx]}...</p>
    </div>
  );
}

function InstructionBox({ value, onChange, hint }) {
  const max = 300;
  const [open, setOpen] = useState(!!(value && value.length));
  return (
    <div className={`instruction ${open ? 'is-open' : ''}`}>
      <button type="button" className="instruction-toggle" onClick={() => setOpen(!open)}>
        <span className="left">
          <span className="ico">
            <svg viewBox="0 0 24 24"><path d="M12 20 h9"/><path d="M16.5 3.5 a2.121 2.121 0 0 1 3 3 L7 19 l-4 1 1-4 z"/></svg>
          </span>
          詳細指示を追加
          <span className="opt">OPTIONAL</span>
        </span>
        <svg className="arrow" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      <div className="instruction-body">
        <textarea
          value={value}
          onChange={e => { if (e.target.value.length <= max) onChange(e.target.value); }}
          placeholder={hint}
        />
        <div className="instruction-meta">
          <span className="tip">記事の方向性・ターゲット・避けたい表現などを自由に指示できます</span>
          <span className="count">{value.length} / {max}</span>
        </div>
      </div>
    </div>
  );
}

function CopyButton({ text, label = 'コピー' }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handle} className={`copy-btn-small ${copied ? 'is-copied' : ''}`}>
      {copied ? '✓ コピーしました' : label}
    </button>
  );
}

function CopyButtonLarge({ text, title, desc, mode, html }) {
  const [copied, setCopied] = useState(false);
  const handle = async () => {
    try {
      if (mode === 'rich' && html) {
        const blob = new Blob([html], { type: 'text/html' });
        const textBlob = new Blob([html.replace(/<[^>]+>/g, '').replace(/\n{3,}/g, '\n\n').trim()], { type: 'text/plain' });
        const clipboardItem = new window.ClipboardItem({
          'text/html': blob,
          'text/plain': textBlob,
        });
        await navigator.clipboard.write([clipboardItem]);
      } else {
        await navigator.clipboard.writeText(text);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        console.error('コピーに失敗しました:', e);
      }
    }
  };
  return (
    <button onClick={handle} className={`copy-btn-large ${copied ? 'is-copied' : ''}`}>
      <div className="ico">
        {mode === 'html' ? (
          <svg viewBox="0 0 24 24"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
        ) : mode === 'rich' ? (
          <svg viewBox="0 0 24 24"><path d="M14 2 H6 a2 2 0 0 0-2 2 v16 a2 2 0 0 0 2 2 h12 a2 2 0 0 0 2-2 V8 z"/><polyline points="14 2 14 8 20 8"/><path d="M9 13 h6 M9 17 h4"/></svg>
        ) : (
          <svg viewBox="0 0 24 24"><path d="M14 2 H6 a2 2 0 0 0-2 2 v16 a2 2 0 0 0 2 2 h12 a2 2 0 0 0 2-2 V8 z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
        )}
      </div>
      <div className="info">
        <div className="ttl">{copied ? '✓ コピーしました' : title}</div>
        <div className="desc">{desc}</div>
      </div>
    </button>
  );
}

function StepBar({ current }) {
  const order = ['keyword', 'title', 'structure', 'article'];
  const labels = { keyword: 'キーワード', title: 'タイトル', structure: '構成', article: '本文' };
  const idx = order.indexOf(current);
  return (
    <div className="step-bar">
      {order.map((k, i) => {
        let cls = 'step-item';
        if (i < idx) cls += ' is-done';
        else if (i === idx) cls += ' is-active';
        if (current === 'article' && i <= idx) cls = 'step-item is-done';
        return (
          <div key={k} className={cls}>
            <span className="lbl">STEP 0{i + 1}</span>
            <span className="nm">{labels[k]}</span>
            <div className="bar" />
          </div>
        );
      })}
    </div>
  );
}

const NAV_ITEMS = [
  { key: 'dashboard', label: 'ダッシュボード', icon: <svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/></svg> },
  { key: 'keyword', label: '新規記事作成', icon: <svg viewBox="0 0 24 24"><path d="M11 4 H4 a2 2 0 0 0-2 2 v14 a2 2 0 0 0 2 2 h14 a2 2 0 0 0 2-2 v-7"/><path d="M18.5 2.5 a2.121 2.121 0 0 1 3 3 L12 15 l-4 1 1-4 z"/></svg> },
    { key: 'analytics', label: '集客ダッシュボード', icon: <svg viewBox="0 0 24 24"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg> },
  { key: 'history', label: '履歴', icon: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> },
  { key: 'settings', label: '院情報', icon: <svg viewBox="0 0 24 24"><path d="M3 9 l9-6 9 6 v11 a2 2 0 0 1-2 2 H5 a2 2 0 0 1-2-2 z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
  { key: 'members', label: 'メンバー', icon: <svg viewBox="0 0 24 24"><path d="M17 21 v-2 a4 4 0 0 0-4-4 H5 a4 4 0 0 0-4 4 v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21 v-2 a4 4 0 0 0-3-3.87"/><path d="M16 3.13 a4 4 0 0 1 0 7.75"/></svg> },
];

const EMPTY_CLINIC = { clinic_name: '', region: '', facility_type: '整骨院', qualifications: '', features: '', specialty: '', staff: '', equipment: '' };

export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [usage, setUsage] = useState(null);
  const [account, setAccount] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [activeClinicId, setActiveClinicId] = useState(null);
  const [editingClinicId, setEditingClinicId] = useState(null);
  const [editingClinic, setEditingClinic] = useState(null);

  // サブアカウント関連のstate
  const [ownerId, setOwnerId] = useState(null);
  const [isSubAccount, setIsSubAccount] = useState(false);
  const [subAccounts, setSubAccounts] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [inviteCodeInput, setInviteCodeInput] = useState('');

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    return () => authListener.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user) loadAllData();
  }, [user]);

  // Stripe決済からの戻りを検知
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      setTimeout(() => { loadAllData(); }, 2000);
      window.history.replaceState({}, '', '/');
      setScreen('dashboard');
    }
  }, [user]);

// アカウント情報+全院をまとめて取得(複数アカウント対応版)
  const loadAllData = async () => {
    if (!user) return;

    const { data: linkData } = await supabase
      .from('account_links')
      .select('owner_id')
      .eq('member_id', user.id)
      .maybeSingle();

    const effectiveOwnerId = linkData?.owner_id || user.id;
    const subStatus = !!linkData;
    setOwnerId(effectiveOwnerId);
    setIsSubAccount(subStatus);

    const { data: clientData } = await supabase.from('clients').select('*').eq('id', effectiveOwnerId).single();
    if (clientData) {
      const plan = clientData.plan || 'free';
      const limits = PLAN_LIMITS[plan] || PLAN_LIMITS.free;
      const articlesLimit = clientData.articles_limit_override ?? limits.articles;
      const structuresLimit = clientData.structures_limit_override ?? limits.structures;
      const clinicLimit = clientData.clinic_limit_override ?? clientData.clinic_limit ?? limits.clinics;
      setAccount({
        plan,
        clinic_limit: clinicLimit,
        accounts_limit: limits.accounts,
        owner_email: clientData.email,
      });
      setUsage({
        articlesUsed: clientData.articles_used_this_month || 0,
        articlesLimit: articlesLimit,
        structuresUsed: clientData.structures_used_this_month || 0,
        structuresLimit: structuresLimit,
      });
    }

    const { data: clinicsData } = await supabase
      .from('clinics')
      .select('*')
      .eq('client_id', effectiveOwnerId)
      .order('created_at', { ascending: true });

    if (clinicsData) {
      setClinics(clinicsData);
      const active = clinicsData.find(c => c.is_active);
      if (active) {
        setActiveClinicId(active.id);
      } else if (clinicsData.length > 0) {
        await switchActiveClinic(clinicsData[0].id, clinicsData, effectiveOwnerId);
      }
    }

    if (clientData) {
      const plan = clientData.plan || 'free';
      if (HISTORY_ENABLED_PLANS.includes(plan)) {
        const { data: historyData } = await supabase
          .from('articles')
          .select('id, title, keyword, persona, created_at, clinic_id')
          .eq('client_id', effectiveOwnerId)
          .order('created_at', { ascending: false })
          .limit(HISTORY_LIMIT);
        if (historyData) setHistory(historyData);
      }
    }

    if (!subStatus) {
      const { data: subsData } = await supabase
        .from('account_links')
        .select('id, member_id, member_email, created_at')
        .eq('owner_id', user.id)
        .order('created_at', { ascending: false });
      if (subsData) setSubAccounts(subsData);

      const { data: invData } = await supabase
        .from('invitations')
        .select('*')
        .eq('owner_id', user.id)
        .is('used_by', null)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });
      if (invData) setInvitations(invData);
    }
  };

  const loadHistory = async () => {
    if (!user || !ownerId) return;
    setHistoryLoading(true);
    const { data, error } = await supabase
      .from('articles')
      .select('id, title, keyword, persona, created_at, clinic_id')
      .eq('client_id', ownerId)
      .order('created_at', { ascending: false })
      .limit(HISTORY_LIMIT);
    if (error) {
      console.error('履歴取得エラー:', error);
      setHistory([]);
    } else {
      setHistory(data || []);
    }
    setHistoryLoading(false);
  };

  const loadHistoryDetail = async (articleId) => {
    const { data, error } = await supabase
      .from('articles')
      .select('*')
      .eq('id', articleId)
      .single();
    if (error) {
      setError('記事の取得に失敗しました');
      return;
    }
    setViewingArticle(data);
  };

  const deleteHistory = async (articleId) => {
    if (!window.confirm('この履歴を削除しますか?この操作は取り消せません。')) return;
    const { error } = await supabase.from('articles').delete().eq('id', articleId);
    if (error) {
      setError('削除エラー:' + error.message);
      return;
    }
    await loadHistory();
    setViewingArticle(null);
  };

  const saveToHistory = async (articleData) => {
    if (!account || !HISTORY_ENABLED_PLANS.includes(account.plan)) return;
    const { error } = await supabase.from('articles').insert({
      client_id: ownerId,
      clinic_id: activeClinicId,
      keyword: articleData.keyword,
      title: articleData.title,
      persona: articleData.persona,
      article_html: articleData.html,
    });
    if (error) {
      console.error('履歴保存エラー:', error);
    }
  };

  const updateLatestHistory = async (html) => {
    if (!account || !HISTORY_ENABLED_PLANS.includes(account.plan)) return;
    if (!user || !ownerId) return;
    const { data } = await supabase
      .from('articles')
      .select('id')
      .eq('client_id', ownerId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();
    if (data) {
      await supabase.from('articles').update({ article_html: html }).eq('id', data.id);
    }
  };

  // ========================================
  // サブアカウント関連の関数
  // ========================================

  const createInvitation = async () => {
    if (!user || isSubAccount) return;
    const currentCount = 1 + subAccounts.length + invitations.length;
    if (currentCount >= account.accounts_limit) {
      setError(`現在のプラン(${PLAN_LABELS[account.plan]})ではアカウントを${account.accounts_limit}件までしか発行できません`);
      return;
    }
    setError('');
    const code = generateInvitationCode();
    const { error } = await supabase.from('invitations').insert({
      owner_id: user.id,
      invitation_code: code,
    });
    if (error) {
      setError('招待コード発行エラー:' + error.message);
      return;
    }
    await loadAllData();
  };

  const deleteInvitation = async (invitationId) => {
    if (!window.confirm('この招待コードを取り消しますか?')) return;
    const { error } = await supabase.from('invitations').delete().eq('id', invitationId);
    if (error) {
      setError('削除エラー:' + error.message);
      return;
    }
    await loadAllData();
  };

  const removeSubAccount = async (linkId, memberEmail) => {
    if (!window.confirm(`${memberEmail} をサブアカウントから解除しますか?\n相手のログインアカウントは残りますが、こちらの院・履歴にはアクセスできなくなります。`)) return;
    const { error } = await supabase.from('account_links').delete().eq('id', linkId);
    if (error) {
      setError('解除エラー:' + error.message);
      return;
    }
    await loadAllData();
  };

  const useInvitationCode = async () => {
    const code = inviteCodeInput.trim();
    if (!code) {
      setError('招待コードを入力してください');
      return;
    }
    setError('');

    const { data: invData, error: invError } = await supabase
      .from('invitations')
      .select('*')
      .eq('invitation_code', code)
      .is('used_by', null)
      .gt('expires_at', new Date().toISOString())
      .maybeSingle();

    if (invError || !invData) {
      setError('招待コードが無効、または有効期限が切れています');
      return;
    }

    if (invData.owner_id === user.id) {
      setError('自分が発行した招待コードは使えません');
      return;
    }

    const { data: existingLink } = await supabase
      .from('account_links')
      .select('id')
      .eq('member_id', user.id)
      .maybeSingle();

    if (existingLink) {
      setError('既に別のオーナーのサブアカウントになっています。先に解除してもらう必要があります');
      return;
    }

    const { error: linkError } = await supabase.from('account_links').insert({
      owner_id: invData.owner_id,
      member_id: user.id,
      member_email: user.email,
    });

    if (linkError) {
      setError('紐付けに失敗しました:' + linkError.message);
      return;
    }

    await supabase.from('invitations').update({
      used_by: user.id,
      used_at: new Date().toISOString(),
    }).eq('id', invData.id);

    setInviteCodeInput('');
    alert('招待コードを使用しました。サブアカウントとしてログインしています。');
    await loadAllData();
  };

  const copyInvitationCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      alert(`招待コードをコピーしました:\n${code}\n\nこのコードを共有する相手に伝えてください。`);
    } catch (e) {
      alert(`コピーに失敗しました。コードを手動でコピーしてください:\n${code}`);
    }
  };

  const loadAdminUsers = async () => {
    if (!account || account.plan !== 'developer') return;
    setAdminLoading(true);
    const { data: usersData, error: usersError } = await supabase
      .from('clients')
      .select('*')
      .order('created_at', { ascending: false });
    if (usersError) {
      console.error('ユーザー取得エラー:', usersError);
      setAdminUsers([]);
      setAdminLoading(false);
      return;
    }
    const userIds = usersData.map(u => u.id);
    const { data: clinicCounts } = await supabase
      .from('clinics')
      .select('client_id')
      .in('client_id', userIds);
    const countMap = {};
    (clinicCounts || []).forEach(c => {
      countMap[c.client_id] = (countMap[c.client_id] || 0) + 1;
    });
    const enriched = usersData.map(u => ({
      ...u,
      clinic_count: countMap[u.id] || 0,
    }));
    setAdminUsers(enriched);
    setAdminLoading(false);
  };

  const startEditUser = (userId) => {
    const u = adminUsers.find(x => x.id === userId);
    if (!u) return;
    setEditingUserId(userId);
    setEditingUserData({
      plan: u.plan || 'free',
      articles_used_this_month: u.articles_used_this_month || 0,
      structures_used_this_month: u.structures_used_this_month || 0,
      articles_limit_override: u.articles_limit_override,
      structures_limit_override: u.structures_limit_override,
      clinic_limit_override: u.clinic_limit_override,
    });
  };

  const saveUserChanges = async () => {
    if (!editingUserId || !editingUserData) return;
    const updates = {
      plan: editingUserData.plan,
      articles_used_this_month: parseInt(editingUserData.articles_used_this_month) || 0,
      structures_used_this_month: parseInt(editingUserData.structures_used_this_month) || 0,
      articles_limit_override: editingUserData.articles_limit_override === '' || editingUserData.articles_limit_override === null ? null : parseInt(editingUserData.articles_limit_override),
      structures_limit_override: editingUserData.structures_limit_override === '' || editingUserData.structures_limit_override === null ? null : parseInt(editingUserData.structures_limit_override),
      clinic_limit_override: editingUserData.clinic_limit_override === '' || editingUserData.clinic_limit_override === null ? null : parseInt(editingUserData.clinic_limit_override),
    };
    const { error } = await supabase.from('clients').update(updates).eq('id', editingUserId);
    if (error) {
      setError('保存エラー:' + error.message);
      return;
    }
    await loadAdminUsers();
    setEditingUserId(null);
    setEditingUserData(null);
    if (editingUserId === user.id) {
      await loadAllData();
    }
  };

  const switchActiveClinic = async (clinicId, clinicsList = null, ownerIdOverride = null) => {
    const list = clinicsList || clinics;
    const targetOwnerId = ownerIdOverride || ownerId;
    await supabase.from('clinics').update({ is_active: false }).eq('client_id', targetOwnerId);
    await supabase.from('clinics').update({ is_active: true }).eq('id', clinicId);
    const updated = list.map(c => ({ ...c, is_active: c.id === clinicId }));
    setClinics(updated);
    setActiveClinicId(clinicId);
  };

  const saveClinic = async () => {
    if (!editingClinic.clinic_name || !editingClinic.region) {
      setError('院名と地域は必須です');
      return false;
    }
    setError('');

    if (editingClinicId === 'new') {
      if (clinics.length >= account.clinic_limit) {
        setError(`現在のプラン(${PLAN_LABELS[account.plan]})では院を${account.clinic_limit}件までしか登録できません`);
        return false;
      }
      const isFirstClinic = clinics.length === 0;
      const { data, error } = await supabase.from('clinics').insert({
        client_id: ownerId,
        clinic_name: editingClinic.clinic_name,
        region: editingClinic.region,
        facility_type: editingClinic.facility_type,
        qualifications: editingClinic.qualifications,
        features: editingClinic.features,
        specialty: editingClinic.specialty,
        staff: editingClinic.staff,
        equipment: editingClinic.equipment,
        is_active: isFirstClinic,
      }).select().single();
      if (error) {
        setError('保存エラー:' + error.message);
        return false;
      }
      await loadAllData();
      setEditingClinicId(data.id);
      setEditingClinic(data);
      return true;
    } else {
      const { error } = await supabase.from('clinics').update({
        clinic_name: editingClinic.clinic_name,
        region: editingClinic.region,
        facility_type: editingClinic.facility_type,
        qualifications: editingClinic.qualifications,
        features: editingClinic.features,
        specialty: editingClinic.specialty,
        staff: editingClinic.staff,
        equipment: editingClinic.equipment,
      }).eq('id', editingClinicId);
      if (error) {
        setError('保存エラー:' + error.message);
        return false;
      }
      await loadAllData();
      return true;
    }
  };

  const deleteClinic = async (clinicId) => {
    if (clinics.length <= 1) {
      setError('最後の1院は削除できません');
      return;
    }
    if (clinicId === activeClinicId) {
      setError('アクティブな院は削除できません。先に別の院に切り替えてください');
      return;
    }
    if (!window.confirm('この院を削除しますか?この操作は取り消せません。')) return;
    const { error } = await supabase.from('clinics').delete().eq('id', clinicId);
    if (error) {
      setError('削除エラー:' + error.message);
      return;
    }
    await loadAllData();
    setEditingClinicId(null);
    setEditingClinic(null);
  };

  // 資格チェックのトグル(editingClinic.qualifications はカンマ区切り文字列)
  const toggleQualification = (q) => {
    const list = (editingClinic.qualifications || '').split(',').map(s => s.trim()).filter(Boolean);
    let next;
    if (list.includes(q)) {
      next = list.filter(x => x !== q);
    } else {
      next = [...list, q];
    }
    setEditingClinic({ ...editingClinic, qualifications: next.join(',') });
  };

  const startEditClinic = (clinicId) => {
    if (clinicId === 'new') {
      setEditingClinicId('new');
      setEditingClinic({ ...EMPTY_CLINIC });
    } else {
      const c = clinics.find(c => c.id === clinicId);
      if (c) {
        setEditingClinicId(clinicId);
        setEditingClinic({
          clinic_name: c.clinic_name || '',
          region: c.region || '',
          facility_type: c.facility_type || '整骨院',
          qualifications: c.qualifications || '',
          features: c.features || '',
          specialty: c.specialty || '',
          staff: c.staff || '',
          equipment: c.equipment || '',
        });
      }
    }
    setError('');
  };

  const checkLimit = async (type) => {
    if (!ownerId) return false;
    const { data, error } = await supabase
      .from('clients')
      .select('articles_used_this_month, structures_used_this_month, last_reset_date, plan, articles_limit_override, structures_limit_override')
      .eq('id', ownerId).single();
    if (error) { console.error('チェックエラー:', error); return false; }
    const plan = data.plan || 'free';
    const limits = PLAN_LIMITS[plan] || PLAN_LIMITS.free;
    const articlesLimit = data.articles_limit_override ?? limits.articles;
    const structuresLimit = data.structures_limit_override ?? limits.structures;
    const today = new Date();
    const lastReset = new Date(data.last_reset_date);
    const isNewMonth = today.getMonth() !== lastReset.getMonth() || today.getFullYear() !== lastReset.getFullYear();
    if (isNewMonth) {
      await supabase.from('clients').update({
        articles_used_this_month: 0, structures_used_this_month: 0,
        last_reset_date: today.toISOString().split('T')[0],
      }).eq('id', ownerId);
      return true;
    }
    if (type === 'article') return data.articles_used_this_month < articlesLimit;
    if (type === 'structure') return data.structures_used_this_month < structuresLimit;
    return false;
  };

  const incrementCount = async (type) => {
    if (!ownerId) return;
    const column = type === 'article' ? 'articles_used_this_month' : 'structures_used_this_month';
    const { data } = await supabase.from('clients').select(column).eq('id', ownerId).single();
    if (data) {
      await supabase.from('clients').update({ [column]: data[column] + 1 }).eq('id', ownerId);
      await loadAllData();
    }
  };

  const [screen, setScreen] = useState('dashboard');
  const [keyword, setKeyword] = useState('');
  const [ctaMode, setCtaMode] = useState('with'); // 'with'=院へ誘導する / 'without'=誘導しない
  const [refMode, setRefMode] = useState('none');  // 'none'=参考文献なし / 'with'=あり
  const [caseMode, setCaseMode] = useState('none');
  const [cases, setCases] = useState([{ region: '', age: '', job: '', symptom: '', concern: '', treatment: '', period: '', result: '', voice: '' }]);
  const [titles, setTitles] = useState([]);
  const [selTitle, setSelTitle] = useState('');
  const [custTitle, setCustTitle] = useState('');
  const [structure, setStructure] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [article, setArticle] = useState('');
  const [media, setMedia] = useState({ hotpepper: '', gmb: '', line: '' });
  const [loading, setLoading] = useState(false);
  const [loadStep, setLoadStep] = useState('title');
  const [tab, setTab] = useState('html');
  const [instructionStep1, setInstructionStep1] = useState('');
  const [instructionStep2, setInstructionStep2] = useState('');
  const [instructionStep3, setInstructionStep3] = useState('');
  const [editedArticle, setEditedArticle] = useState('');
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [viewingArticle, setViewingArticle] = useState(null);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingUserData, setEditingUserData] = useState(null);
  const [adminSearch, setAdminSearch] = useState('');

  const activeClinic = clinics.find(c => c.id === activeClinicId);
  const settings = activeClinic ? {
    clinicName: activeClinic.clinic_name || '',
    area: activeClinic.region || '',
    facility: activeClinic.facility_type || '整骨院',
    qualifications: activeClinic.qualifications || '',
    features: activeClinic.features || '',
    specialty: activeClinic.specialty || '',
    staff: activeClinic.staff || '',
    equipment: activeClinic.equipment || '',
  } : { clinicName: '', area: '', facility: '整骨院', qualifications: '', features: '', specialty: '', staff: '', equipment: '' };

  const callAPI = async (messages, maxTokens = 2000, retries = 3) => {
    for (let i = 0; i < retries; i++) {
      const res = await fetch('/api/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model: 'claude-sonnet-4-5', max_tokens: maxTokens, system: SYSTEM_PROMPT, messages }),
      });
      const rawText = await res.text();
      let data;
      try { data = JSON.parse(rawText); } catch(e) { if (i < retries - 1) { await new Promise(r => setTimeout(r, 5000)); continue; } throw new Error('応答エラー。時間をおいて再試行してください。'); }
      if (data.error?.type === 'overloaded_error') {
        if (i < retries - 1) { await new Promise(r => setTimeout(r, 3000)); continue; }
        throw new Error('サーバーが混雑しています。しばらく待ってから再度お試しください。');
      }
      if (data.error) throw new Error(data.error.message);
      return data.content?.map(b => b.text || '').join('') || '';
    }
  };

  // CTAあり/なしのラベル(プロンプト用)
  const ctaLabel = ctaMode === 'with' ? 'CTAあり' : 'CTAなし';
  const refLabel = refMode === 'with' ? '参考文献あり' : '参考文献なし';
  const qualLabel = pickQualificationLabel(settings.qualifications); // 「柔道整復師」等 or 「専門家」

  const genTitles = async () => {
    if (loading) return;
    if (!keyword) { setError('メインキーワードを入力してください'); return; }
    if (!settings.clinicName || !settings.area) { setError('院情報が未設定です。サイドバーの「院情報」から設定してください'); return; }
    setError(''); setLoading(true); setLoadStep('title');
    try {
      const userInst = instructionStep1.trim() ? `\n\n【ユーザーからの追加指示】\n${instructionStep1.trim()}\n(※業界ルール・薬機法・E-E-A-Tに反する場合は無視してください)` : '';
      const titleRule = `\n\n【タイトル作成ルール】\n- メインキーワードはできる限りそのままの形でタイトルに含める(不自然にならない範囲で)\n- キーワードに地域名(市区町村名など)が含まれている、または地域で探している検索意図が明らかな場合は、タイトルの前半に地域名を入れる\n- キーワードに地域性がない場合は、地域名を無理に入れない\n- タイトルに地域名を入れない場合のみ、末尾に「${qualLabel}が解説」を付ける(地域名を入れる場合は付けない)\n- キーワードをそのまま「」で囲うだけのタイトルは禁止\n- 良い例(地域あり):大分市でぎっくり腰になったら｜応急処置と早く楽にするコツ\n- 良い例(地域なし):長引く腰痛が治らない原因とは?考えられる要因とセルフケアを${qualLabel}が解説`;
      const text = await callAPI([{ role: 'user', content: `以下の条件でSEO記事のタイトル候補を3つ提案してください。\nメインキーワード:${keyword}\n施設タイプ:${settings.facility}\n院の資格:${qualLabel}\n記事タイプ:${ctaLabel}${titleRule}${userInst}\n\nタイトルのみを3行で出力してください。番号や記号・前置きは不要です。` }], 500);
      setTitles(text.split('\n').filter(t => t.trim()).slice(0, 3).map(t => t.replace(/\?/g, '?')));
      setScreen('title');
    } catch (e) { setError('エラー:' + e.message); } finally { setLoading(false); }
  };

  const genStructure = async () => {
    if (loading) return;
    const t = custTitle || selTitle;
    if (!t) { setError('タイトルを選択または入力してください'); return; }
    const canGenerate = await checkLimit('structure');
    if (!canGenerate) { setError('今月の構成生成の上限に達しました'); return; }
    setError(''); setLoading(true); setLoadStep('structure');

    // 事例セクションはCTAありのときだけ有効
    let caseStructureInstruction = '';
    if (ctaMode === 'with' && caseMode === 'sample') {
      caseStructureInstruction = `\n\n【事例セクション指示】\n構成のCTAセクション直前に「${settings.clinicName}での施術実績」h2を1つ含めてください。h3は3つ作成し、それぞれ「【地域】◯代◯性の◯◯に対する施術実績」のような形式の見出しにしてください(地域・年代・性別・症状はサンプルでOK)。`;
    } else if (ctaMode === 'with' && caseMode === 'real' && cases.length > 0) {
      const caseHeadings = cases.map(c => `【${c.region || '地域'}】${c.age || ''}の${c.symptom || ''}に対する施術実績`).join('、');
      caseStructureInstruction = `\n\n【事例セクション指示】\n構成のCTAセクション直前に「${settings.clinicName}での施術実績」h2を1つ含めてください。h3は必ず${cases.length}個だけ作成し、勝手に追加・水増し禁止です。h3の形式は以下の通りにしてください:\n${caseHeadings}`;
    } else {
      caseStructureInstruction = '\n\n【重要】事例セクションを構成に含めないでください。';
    }

    // CTAあり/なしで最後のh2の指示を変える
    const ctaStructureRule = ctaMode === 'with'
      ? `\n- 最後のh2は必ず「【症状】でお悩みなら【地域】の【院名】へ」形式(例:腰痛でお悩みなら大分市のゆらぎ整骨院へ)\n- 事例セクションのh2は「【院名】での施術実績」形式(例:大分市ゆらぎ整骨院での施術実績)`
      : `\n- 最後のh2は「まとめ」にする(内容的にセルフケアや受診の目安の方がふさわしければそれでもよい)\n- 本文中で来院を促す勧誘表現は使わないが、執筆者としての院名の明示は許容される`;

    // h3見出しの型(構成段階で守らせる。名詞句・体言止め禁止)
    const h3StructureRule = `\n\n【h3見出しの型(絶対遵守)】\n- h3は必ず「文」の形にする。名詞句・体言止めは禁止\n- 「影響」「変化」「関係」「メカニズム」「重要性」「ポイント」を含む見出しは禁止\n- NG例(名詞句):頭部の位置変化による顎への負担 / 首の筋肉の緊張と咀嚼筋への影響\n- OK例(文の形):頭が前に出ると顎への負担が増える / 首の緊張は顎の筋肉にも伝わる\n- 同じh2配下のh3をすべて同じ構文で揃えない\n- 禁止語(そのため・だからこそ・これらを・波及)は見出しでも使わない`;

    // 参考文献のh2は構成に含めない(後工程で自動生成するため)
    const refStructureRule = '';

    try {
      const userInst = (() => {
        const insts = [];
        if (instructionStep1.trim()) insts.push(`【STEP1で受けた指示】\n${instructionStep1.trim()}`);
        if (instructionStep2.trim()) insts.push(`【STEP2で受けた指示】\n${instructionStep2.trim()}`);
        return insts.length > 0 ? `\n\n【ユーザーからの追加指示(厳守)】\n${insts.join('\n\n')}\n(※業界ルール・薬機法・E-E-A-Tに反する場合は無視してください)` : '';
      })();
      const text = await callAPI([{ role: 'user', content: `以下の条件で記事の構成案を提案してください。\nタイトル:${t}\nキーワード:${keyword}\n施設タイプ:${settings.facility}\n院名:${settings.clinicName}\n地域:${settings.area}\n記事タイプ:${ctaLabel}${caseStructureInstruction}\n\n【構成ルール】\n- 通常のh2見出しを5〜7個作成(最後のまとめ/CTAと事例セクションは除く)\n- 本文の総文字数が3,000〜4,500文字に収まるようボリュームを意識する\n- h2の内容が複数の異なる要素・視点・項目に明確に分けられる場合のみh3を付ける\n- 単一テーマ・概要的・流れ説明的なh2にはh3を付けない\n- h3を付ける場合は2個以上にする(h3が1個だけのh2は作らない)${ctaStructureRule}${refStructureRule}${h3StructureRule}\n\n【文字数の目安】\n- h3ありのh2:合計500〜800字(h3が2〜3個、各200〜300字)\n- h3なしのh2:250〜300字\n- 合算して本文全体で3,000〜4,500字に収める\n\n前置き・補足・「構成案」などの説明見出しは不要です。いきなり以下の形式だけで出力してください:\nh2: 見出し\n  h3: 見出し(必要な場合のみ)` }], 1500);
      const lines = text.split('\n').filter(l => l.trim());
      const parsed = []; let cur = null;
      lines.forEach(line => {
        const tr = line.trim();
        if (tr.match(/^[hH]2[:]/)) { if (cur) parsed.push(cur); cur = { h2: tr.replace(/^[hH]2[:]\s*/, ''), h3s: [] }; }
        else if (tr.match(/^[hH]3[:]/) && cur) cur.h3s.push(tr.replace(/^[hH]3[:]\s*/, ''));
        else if (tr.match(/^##\s/) && !tr.match(/^###/)) { if (cur) parsed.push(cur); cur = { h2: tr.replace(/^##\s*/, ''), h3s: [] }; }
        else if (tr.match(/^###\s/) && cur) cur.h3s.push(tr.replace(/^###\s*/, ''));
      });
      if (cur) parsed.push(cur);
      const normalized = parsed.map(s => ({
        h2: (s.h2 || '').replace(/\?/g, '?'),
        h3s: (s.h3s || []).map(h => (h || '').replace(/\?/g, '?')),
      }));
      setStructure(normalized); await incrementCount('structure'); setScreen('structure');
    } catch (e) { setError('エラー:' + e.message); } finally { setLoading(false); }
  };

  const genArticle = async () => {
    if (loading) return;
    const t = custTitle || selTitle;
    const canGenerate = await checkLimit('article');
    if (!canGenerate) { setError('今月の本文生成の上限に達しました'); return; }
    setError(''); setLoading(true); setLoadStep('article');
    const st = structure.map(s => {
      if (s.h3s && s.h3s.length > 0) {
        return `h2: ${s.h2}(h3あり:${s.h3s.length}個)\n${s.h3s.map(h => `  h3: ${h}`).join('\n')}`;
      } else {
        return `h2: ${s.h2}(h3なし/このセクションにはh3を絶対に追加しない)`;
      }
    }).join('\n');

    // 事例はCTAありのときだけ
    let caseInstruction = '';
    if (ctaMode === 'with' && caseMode === 'sample') {
      caseInstruction = `\n\n【事例セクション指示】\n「${settings.clinicName}での施術実績」h2セクション内にサンプル事例を3件作成してください。各事例のh3見出しは「【地域】◯代◯性の◯◯に対する施術実績」形式にしてください。各事例の本文には、原因→施術内容→通院期間→結果→患者の感想の流れで自然な文章を作成してください。`;
    } else if (ctaMode === 'with' && caseMode === 'real' && cases.length > 0) {
      const caseDetails = cases.map((c, i) => `事例${i + 1}:\n- 居住地域:${c.region}\n- 年代・性別:${c.age}\n- 職業:${c.job || '記載なし'}\n- 主訴・症状:${c.symptom}\n- 困っていたこと:${c.concern}\n- 施術内容:${c.treatment}\n- 通院期間・回数:${c.period}\n- 結果・変化:${c.result}\n- 患者の感想:${c.voice || '記載なし'}`).join('\n\n');
      caseInstruction = `\n\n【事例セクション指示】\n以下の事例情報をもとに、「${settings.clinicName}での施術実績」h2セクション内に事例を作成してください。\n\n重要ルール:\n- 提供された事例は${cases.length}件です。必ず${cases.length}件だけ作成し、勝手に水増し・追加することは絶対禁止です。\n- 各事例のh3見出しは「【地域】◯代◯性の◯◯に対する施術実績」形式にしてください。\n- 提供された情報を忠実に反映し、勝手に内容を改変・捏造しないでください。\n\n${caseDetails}\n\n上記の情報を自然な事例文に整形してください。原因→施術内容→通院期間→結果の流れで構成し、患者の感想は引用形式で入れてください。`;
    } else {
      caseInstruction = '\n\n【重要】事例セクション・症例・体験談・お客様の声などのセクションを記事に含めないでください。';
    }

    // CTAあり/なしの本文指示
    const ctaArticleRule = ctaMode === 'with'
      ? `\n\n【記事タイプ:CTAあり】\n- 冒頭文は「本ページでは、【地域】の【院名】が、〜について解説します。」の形で締める\n- 冒頭文に、院の一次情報を示す一文を1つ入れる(例:当院にも同じ悩みで来院される人がいらっしゃいます / 当院では【症状】の施術を多く経験していますが)。文型は毎回変える。具体的な件数・年代・個別症例は作らない\n- 記事の最後は「【症状】でお悩みなら【地域】の【院名】へ」形式のCTAで締める\n- 院名は院情報の院名をそのまま使う`
      : `\n\n【記事タイプ:CTAなし】\n- 冒頭文は「本ページでは、【地域】の【院名】が、〜について解説します。」の形で締める(執筆者の明示なので院名を出してよい)\n- 本文中で来院を促す勧誘表現(「〜へお越しください」等)は使わない\n- 事例・体験談・お客様の声は入れない\n- 最後は「まとめ」で締める(内容次第でセルフケアや受診の目安でもよい)`;

    // 参考文献の本文指示(URLは生成させない。番号のみ。出典h2は後工程で生成)
    const refArticleRule = refMode === 'with'
      ? `\n\n【参考文献:あり】\n- 医学的・統計的な主張をした文の末尾にのみ(※1)(※2)のように全角括弧の通し番号を付ける\n- URLや出典名は絶対に生成しない(実在確認ができないため)。参考文献のh2も作らない(後工程で自動生成される)`
      : `\n\n【参考文献:なし】\n- (※1)等の参照番号は一切付けない`;

    try {
      const userInst = (() => {
        const insts = [];
        if (instructionStep1.trim()) insts.push(`【STEP1で受けた指示】\n${instructionStep1.trim()}`);
        if (instructionStep2.trim()) insts.push(`【STEP2で受けた指示】\n${instructionStep2.trim()}`);
        if (instructionStep3.trim()) insts.push(`【STEP3で受けた指示】\n${instructionStep3.trim()}`);
        return insts.length > 0 ? `\n\n【ユーザーからの追加指示(厳守)】\n${insts.join('\n\n')}\n(※業界ルール・薬機法・E-E-A-Tに反する場合は無視してください)` : '';
      })();
      const text = await callAPI([{ role: 'user', content: `【最優先ルール・絶対遵守】\n以下のタイトルと構成は、ユーザー本人が入力・編集したものです。1文字も改変せず、そのまま使用してください。\n- 指定タイトル:「${t}」 ← このまま<h1>タグに使う\n- 指定構成:下記の構成をそのまま使う(h2/h3の文言・順序・数を一切変えない)\n\n以下の条件でSEO記事をHTML形式で作成してください。\n\n【院情報】\n院名:${settings.clinicName}\n地域:${settings.area}\n施設タイプ:${settings.facility}\n院の資格:${qualLabel}\n院の強み:${settings.features}\n得意な症状・施術:${settings.specialty}\nスタッフ情報:${settings.staff}\n設備・機器:${settings.equipment}\n\n【記事情報】\nタイトル(変更厳禁):${t}\nキーワード:${keyword}\n\n【構成(厳守・変更厳禁)】\n${st}${caseInstruction}${ctaArticleRule}${refArticleRule}\n\n【構成厳守の最重要ルール】\n- 上記の構成を1文字も改変せず、そのまま使用\n- 「h3あり」のh2には指定されたh3のみ使用\n- 「h3なし」のh2にはh3を絶対に追加しない(p段落のみで展開)\n- 構成にないh2・h3を勝手に追加、順序変更は絶対禁止\n- h3を持つh2では「リード文(1〜2文のp)→箇条書き→本文」の順で展開\n\n【文字数の最重要ルール】\n- 本文の総文字数は3,000〜4,500字に必ず収める(HTMLタグ除く)\n- 各h3の本文は200〜300字以内/h3なしのh2は250〜300字以内\n- 冗長な文・繰り返しは禁止${userInst}\n\n上記を厳守してSEO記事を作成してください。タイトルはh1タグで出力。HTMLのみをそのまま出力してください。` }], 8000);
      let cleaned = text.replace(/```html/g, '').replace(/```/g, '').replace(/<p>&nbsp;<\/p>/g, '').replace(/<p>\s*<\/p>/g, '').trim();

      if (t) {
        if (/<h1[^>]*>[\s\S]*?<\/h1>/i.test(cleaned)) {
          cleaned = cleaned.replace(/<h1[^>]*>[\s\S]*?<\/h1>/i, `<h1>${t}</h1>`);
        } else {
          cleaned = `<h1>${t}</h1>\n` + cleaned;
        }
      }
      cleaned = cleaned.replace(/\?/g, '?');

      setArticle(cleaned); setEditedArticle(cleaned); await incrementCount('article');
      await saveToHistory({
        keyword: keyword,
        title: t,
        persona: ctaLabel,
        html: cleaned,
      });
      setScreen('article');
    } catch (e) { setError('エラー:' + e.message); } finally { setLoading(false); }
  };

  const genMedia = async (type) => {
    setLoading(true); setLoadStep('media');
    const symptom = keyword;
    const area = settings.area;
    const cfg = {
      hotpepper: { label: 'ホットペッパー', chars: 1000, guide: `ホットペッパービューティー用の投稿文を作成してください。見出しは使えないため、セクションの区切りに【】で囲ったタイトルを使用してください(例:【${symptom}でお悩みの方へ】)。ブログへの誘導は不要。文末に#${area} #${symptom}などを含むハッシュタグを7〜8個。` },
      gmb: { label: 'Googleマイビジネス', chars: 1500, guide: `Googleマイビジネスの投稿用文を作成してください。MEO対策として「${area}」「${symptom}」という地域名と症状名を文章の中に自然に複数回盛り込んでください。「${area}で${symptom}でお悩みの方は〜」のような形で地域と症状を組み合わせた表現を積極的に使うこと。文末に「詳しくはこちら→ブログURL」で誘導。文末に#${area} #${symptom}などを含むハッシュタグを7〜8個。` },
      line: { label: 'LINE配信', chars: 500, guide: '絵文字を適度に使う。親しみやすい文体。文末に「続きはこちら→URL」でブログへ誘導。' },
    };
    const c = cfg[type];
    try {
      const text = await callAPI([{ role: 'user', content: `以下の記事を${c.label}用に${c.chars}文字以内で要約してください。\n${c.guide}\nコピーにならないよう表現を変えて要約すること。\n\n【元記事】\n${editedArticle || article}` }], 1000);
      setMedia(prev => ({ ...prev, [type]: text }));
    } catch (e) { setError('エラー:' + e.message); } finally { setLoading(false); }
  };

  const handleUpgrade = async (plan) => {
    if (isSubAccount) { setError('サブアカウントはプラン変更できません。オーナーにご依頼ください。'); return; }
    setError('');
    try {
      const clientData = await supabase.from('clients').select('stripe_customer_id').eq('id', ownerId).single();
      const res = await fetch('/api/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan,
          userId: ownerId,
          userEmail: user.email,
          customerId: clientData.data?.stripe_customer_id || null,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError('決済ページの作成に失敗しました:' + (data.error || ''));
      }
    } catch (e) {
      setError('エラー:' + e.message);
    }
  };

  const resetForNew = () => {
    setKeyword(''); setCtaMode('with'); setRefMode('none'); setCaseMode('none');
    setCases([{ region: '', age: '', job: '', symptom: '', concern: '', treatment: '', period: '', result: '', voice: '' }]);
    setTitles([]); setSelTitle(''); setCustTitle('');
    setStructure([]); setArticle(''); setMedia({ hotpepper: '', gmb: '', line: '' });
    setInstructionStep1(''); setInstructionStep2(''); setInstructionStep3(''); setEditedArticle('');
    setError('');
    setScreen('keyword');
  };

  const handleNav = (key) => {
    setError('');
    setScreen(key);
    if (key === 'settings' && activeClinicId) {
      startEditClinic(activeClinicId);
    } else if (key === 'settings' && !activeClinicId) {
      startEditClinic('new');
    }
    if (key === 'history' && account && HISTORY_ENABLED_PLANS.includes(account.plan)) {
      loadHistory();
    }
    if (key === 'admin' && account && account.plan === 'developer') {
      loadAdminUsers();
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  const handleSwitchClinic = async (clinicId) => {
    if (clinicId === activeClinicId) return;
    await switchActiveClinic(clinicId);
  };

  if (authLoading) return <div className="auth-loading">読み込み中...</div>;
  if (!user) return <Login onLoginSuccess={(u) => setUser(u)} />;

  const isInGenFlow = ['keyword', 'title', 'structure', 'article'].includes(screen);
  const userInitial = (user?.email || '匠').charAt(0).toUpperCase();
  const userName = user?.email?.split('@')[0] || 'ユーザー';

  const articlePct = usage ? Math.min(100, Math.round((usage.articlesUsed / Math.max(usage.articlesLimit, 1)) * 100)) : 0;
  const structurePct = usage ? Math.min(100, Math.round((usage.structuresUsed / Math.max(usage.structuresLimit, 1)) * 100)) : 0;

  const canAddClinic = account && clinics.length < account.clinic_limit;
  const currentAccountCount = 1 + subAccounts.length + invitations.length;
  const canInvite = account && !isSubAccount && currentAccountCount < account.accounts_limit;

  // 現在の資格チェック配列(院情報編集用)
  const editingQualList = (editingClinic?.qualifications || '').split(',').map(s => s.trim()).filter(Boolean);

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <div className="name"><strong>THERAPI WRITER</strong></div>
          <div className="sub">SEO ARTICLE GENERATOR</div>
        </div>
        <nav className="sidebar-nav">
          <div className="label">MENU</div>
          {NAV_ITEMS.map(item => {
            if (item.key === 'members' && isSubAccount) return null;
            const active = item.key === 'keyword' ? isInGenFlow : screen === item.key;
            return (
              <button key={item.key} className={active ? 'active' : ''} onClick={() => handleNav(item.key)}>
                {item.icon}<span>{item.label}</span>
              </button>
            );
          })}
          {account && account.plan === 'developer' && (
            <button className={`admin-nav-btn ${screen === 'admin' ? 'active' : ''}`} onClick={() => handleNav('admin')}>
              <svg viewBox="0 0 24 24"><path d="M12 22 s8-4 8-10 V5 l-8-3-8 3 v7 c0 6 8 10 8 10 z"/></svg>
              <span>ADMIN</span>
              <span className="admin-dot" />
            </button>
          )}
          <div className="divider" />
          <button className={screen === 'preferences' ? 'active' : ''} onClick={() => handleNav('preferences')}>
            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15 a1.65 1.65 0 0 0 .33 1.82 l.06.06 a2 2 0 0 1 0 2.83 a2 2 0 0 1-2.83 0 l-.06-.06 a1.65 1.65 0 0 0-1.82-.33 a1.65 1.65 0 0 0-1 1.51 V21 a2 2 0 0 1-4 0 v-.09 A1.65 1.65 0 0 0 9 19.4 a1.65 1.65 0 0 0-1.82.33 l-.06.06 a2 2 0 0 1-2.83 0 a2 2 0 0 1 0-2.83 l.06-.06 a1.65 1.65 0 0 0 .33-1.82 a1.65 1.65 0 0 0-1.51-1 H3 a2 2 0 0 1 0-4 h.09 A1.65 1.65 0 0 0 4.6 9 a1.65 1.65 0 0 0-.33-1.82 l-.06-.06 a2 2 0 0 1 0-2.83 a2 2 0 0 1 2.83 0 l.06.06 a1.65 1.65 0 0 0 1.82.33 H9 a1.65 1.65 0 0 0 1-1.51 V3 a2 2 0 0 1 4 0 v.09 a1.65 1.65 0 0 0 1 1.51 a1.65 1.65 0 0 0 1.82-.33 l.06-.06 a2 2 0 0 1 2.83 0 a2 2 0 0 1 0 2.83 l-.06.06 a1.65 1.65 0 0 0-.33 1.82 V9 a1.65 1.65 0 0 0 1.51 1 H21 a2 2 0 0 1 0 4 h-.09 a1.65 1.65 0 0 0-1.51 1 z"/></svg>
            <span>設定</span>
          </button>
        </nav>

        {isSubAccount && account?.owner_email && (
          <div className="sub-account-notice">
            <div className="meta">SUB ACCOUNT</div>
            <div className="info">オーナー: {account.owner_email}</div>
          </div>
        )}

        {clinics.length > 0 && (
          <div className="sidebar-clinics">
            <div className="meta">CLINICS <span className="count">{clinics.length}/{account?.clinic_limit === 9999 ? '∞' : account?.clinic_limit}</span></div>
            <div className="clinic-list">
              {clinics.map(c => (
                <button
                  key={c.id}
                  className={`clinic-item ${c.id === activeClinicId ? 'is-active' : ''}`}
                  onClick={() => handleSwitchClinic(c.id)}
                  title={c.id === activeClinicId ? '現在使用中の院' : 'クリックで切替'}
                >
                  <span className="dot" />
                  <div className="info">
                    <div className="name">{c.clinic_name}</div>
                    <div className="place">{c.region} / {c.facility_type}</div>
                  </div>
                </button>
              ))}
              {canAddClinic && (
                <button className="clinic-add" onClick={() => { setScreen('settings'); startEditClinic('new'); }}>
                  + 新しい院を追加
                </button>
              )}
            </div>
          </div>
        )}

        <div className="sidebar-foot">
          <div className="avatar">{userInitial}</div>
          <div className="who">
            <div className="who-name">{userName}</div>
            <div className="who-role">{account ? PLAN_LABELS[account.plan] : 'ADMIN'}{isSubAccount && ' (サブ)'}</div>
          </div>
          <button className="logout" onClick={handleLogout} title="ログアウト">
            <svg viewBox="0 0 24 24"><path d="M9 21 H5 a2 2 0 0 1-2-2 V5 a2 2 0 0 1 2-2 h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          </button>
        </div>
      </aside>

      <main className="main">
        {/* DASHBOARD */}
        {screen === 'dashboard' && (
          <>
            <div className="page-head">
              <div>
                <div className="crumb">DASHBOARD</div>
                <h1>こんにちは、{userName}さん</h1>
                <p>今月もお疲れさまです。最近の活動と使用状況を確認できます。</p>
              </div>
            </div>

            {clinics.length === 0 && (
              <div className="note-box warning">
                <div>
                  院情報がまだ登録されていません。記事生成を始める前に、サイドバーの「院情報」から院の情報を登録してください。
                </div>
              </div>
            )}

            {usage && (
              <div className="usage-grid">
                <div className="usage-card">
                  <div className="top">
                    <div className="label">本文記事</div>
                    <div className="label-icon">
                      <svg viewBox="0 0 24 24"><path d="M14 2 H6 a2 2 0 0 0-2 2 v16 a2 2 0 0 0 2 2 h12 a2 2 0 0 0 2-2 V8 z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/></svg>
                    </div>
                  </div>
                  <div className="num">{usage.articlesUsed}<em>/{usage.articlesLimit === 9999 ? '∞' : usage.articlesLimit}</em><span>本</span></div>
                  <div className="bar"><div className="bar-fill" style={{ width: `${articlePct}%` }} /></div>
                  <div className="remaining"><span>使用率 {articlePct}%</span><strong>残り {usage.articlesLimit === 9999 ? '無制限' : (usage.articlesLimit - usage.articlesUsed) + '本'}</strong></div>
                </div>
                <div className="usage-card">
                  <div className="top">
                    <div className="label">構成生成</div>
                    <div className="label-icon">
                      <svg viewBox="0 0 24 24"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="14" y2="18"/></svg>
                    </div>
                  </div>
                  <div className="num">{usage.structuresUsed}<em>/{usage.structuresLimit === 9999 ? '∞' : usage.structuresLimit}</em><span>回</span></div>
                  <div className="bar"><div className="bar-fill" style={{ width: `${structurePct}%` }} /></div>
                  <div className="remaining"><span>使用率 {structurePct}%</span><strong>残り {usage.structuresLimit === 9999 ? '無制限' : (usage.structuresLimit - usage.structuresUsed) + '回'}</strong></div>
                </div>
              </div>
            )}

            <div className="cta-card">
              <div className="cta-text">
                <div className="cta-eyebrow">QUICK ACTION</div>
                <h2>新しい記事を作成しましょう</h2>
                <div className="desc">キーワードを入力するだけで、4ステップで記事が完成します。</div>
              </div>
              <button className="cta-btn" onClick={resetForNew}>
                記事を作成する
                <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </button>
            </div>

            <div className="section-head">
              <h3>最近の記事</h3>
              {account && HISTORY_ENABLED_PLANS.includes(account.plan) && history.length > 0 && (
                <button className="more" onClick={() => handleNav('history')}>すべて見る →</button>
              )}
            </div>
            {account && HISTORY_ENABLED_PLANS.includes(account.plan) ? (
              history.length === 0 ? (
                <div className="placeholder-card">
                  <strong>まだ履歴がありません</strong>
                  記事を生成すると、ここに自動で履歴が保存されます。
                </div>
              ) : (
                <div className="history-list">
                  {history.slice(0, 3).map(h => {
                    const clinic = clinics.find(c => c.id === h.clinic_id);
                    const date = new Date(h.created_at);
                    const dateStr = `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
                    return (
                      <div key={h.id} className="history-item" onClick={() => { handleNav('history'); setTimeout(() => loadHistoryDetail(h.id), 100); }}>
                        <div className="history-item-main">
                          <div className="history-item-title">{h.title || '(タイトルなし)'}</div>
                          <div className="history-item-meta">
                            <span className="kw">{h.keyword}</span>
                            {clinic && <span className="clinic">{clinic.clinic_name}</span>}
                            <span className="date">{dateStr}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            ) : (
              <div className="placeholder-card">
                <strong>履歴機能はスタンダードプラン以上で利用できます</strong>
                記事を最大{HISTORY_LIMIT}件まで保存できる便利な機能です。
                <div><span className="tag">SCALE UP</span></div>
              </div>
            )}
          </>
        )}

        {/* CLINIC SETTINGS (院情報) */}
        {screen === 'settings' && (
          <>
            <div className="page-head">
              <div>
                <div className="crumb">CLINIC INFO</div>
                <h1>院情報の管理</h1>
                <p>院の情報を登録・編集します。プランに応じて複数の院を登録できます。</p>
              </div>
            </div>

            {clinics.length > 0 && (
              <div className="clinics-list-section">
                <div className="section-head">
                  <h3>登録済みの院 <span className="muted">({clinics.length}/{account?.clinic_limit === 9999 ? '∞' : account?.clinic_limit})</span></h3>
                </div>
                <div className="clinics-grid">
                  {clinics.map(c => (
                    <div
                      key={c.id}
                      className={`clinic-card ${c.id === activeClinicId ? 'is-active' : ''} ${c.id === editingClinicId ? 'is-editing' : ''}`}
                    >
                      <div className="clinic-card-head">
                        <div className="clinic-card-info">
                          <div className="clinic-card-name">{c.clinic_name}</div>
                          <div className="clinic-card-meta">{c.region} / {c.facility_type}</div>
                        </div>
                        {c.id === activeClinicId && <span className="badge-active">使用中</span>}
                      </div>
                      <div className="clinic-card-actions">
                        {c.id !== activeClinicId && (
                          <button className="card-btn" onClick={() => handleSwitchClinic(c.id)}>切替</button>
                        )}
                        <button className={`card-btn ${c.id === editingClinicId ? 'is-active' : ''}`} onClick={() => startEditClinic(c.id)}>
                          {c.id === editingClinicId ? '編集中' : '編集'}
                        </button>
                        {clinics.length > 1 && c.id !== activeClinicId && (
                          <button className="card-btn danger" onClick={() => deleteClinic(c.id)}>削除</button>
                        )}
                      </div>
                    </div>
                  ))}
                  {canAddClinic && (
                    <button className={`clinic-card add-card ${editingClinicId === 'new' ? 'is-editing' : ''}`} onClick={() => startEditClinic('new')}>
                      <div className="add-icon">+</div>
                      <div className="add-text">新しい院を追加</div>
                    </button>
                  )}
                </div>
                {!canAddClinic && account && (
                  <div className="note-box" style={{ marginTop: 12 }}>
                    <div>現在のプラン({PLAN_LABELS[account.plan]})での院登録上限({account.clinic_limit}件)に達しています。</div>
                  </div>
                )}
              </div>
            )}

            {editingClinic && (
              <>
                <div className="section-head" style={{ marginTop: 24 }}>
                  <h3>{editingClinicId === 'new' ? '新しい院を追加' : '院情報を編集'}</h3>
                </div>

                <div className="form-block">
                  <div className="form-head">
                    <div className="num">01</div>
                    <div>
                      <h3>基本情報 <span className="badge-required">必須</span></h3>
                      <p>院名と地域は記事の信頼性に直接関わる項目です。</p>
                    </div>
                  </div>
                  <div className="field-grid-2">
                    <div className="field">
                      <label className="field-lbl">院名<span className="req">*</span></label>
                      <input className="input-text input-small" value={editingClinic.clinic_name} onChange={e => setEditingClinic({ ...editingClinic, clinic_name: e.target.value })} placeholder="例:ABC整骨院" />
                    </div>
                    <div className="field">
                      <label className="field-lbl">地域<span className="req">*</span></label>
                      <input className="input-text input-small" value={editingClinic.region} onChange={e => setEditingClinic({ ...editingClinic, region: e.target.value })} placeholder="例:大阪府中央区" />
                    </div>
                  </div>
                  <div className="field">
                    <label className="field-lbl">施設タイプ</label>
                    <div className="facility-btns">
                      {FACILITY_TYPES.map(f => <button key={f} className={`facility-btn ${editingClinic.facility_type === f ? 'is-active' : ''}`} onClick={() => setEditingClinic({ ...editingClinic, facility_type: f })}>{f}</button>)}
                    </div>
                    {editingClinic.facility_type === 'その他' && (
                      <input className="input-text input-small" style={{ marginTop: 10 }} value={editingClinic.facility_type_custom || ''} onChange={e => setEditingClinic({ ...editingClinic, facility_type_custom: e.target.value })} placeholder="施設タイプを入力(例:カイロプラクティック院)" />
                    )}
                  </div>
                  <div className="field">
                    <label className="field-lbl">保有資格（記事末尾の「◯◯が解説」に使われます・複数選択可）</label>
                    <div className="facility-btns">
                      {QUALIFICATIONS.map(q => (
                        <button
                          key={q}
                          className={`facility-btn ${editingQualList.includes(q) ? 'is-active' : ''}`}
                          onClick={() => toggleQualification(q)}
                        >
                          {editingQualList.includes(q) ? '✓ ' : ''}{q}
                        </button>
                      ))}
                    </div>
                    <p style={{ fontSize: 11, color: 'var(--text-mute)', marginTop: 8 }}>
                      国家資格をお持ちの場合のみ選択してください。未選択の場合、記事では「専門家が解説」と表記されます。
                    </p>
                  </div>
                </div>

                <div className="form-block">
                  <div className="form-head">
                    <div className="num">02</div>
                    <div>
                      <h3>院の特色・強み</h3>
                      <p>記事のCTAや内容に自然に反映されます。</p>
                    </div>
                  </div>
                  <div className="field">
                    <label className="field-lbl">院の強み・特徴</label>
                    <input className="input-text input-small" value={editingClinic.features} onChange={e => setEditingClinic({ ...editingClinic, features: e.target.value })} placeholder="例:完全予約制、女性スタッフ在籍" />
                  </div>
                  <div className="field">
                    <label className="field-lbl">得意な症状・施術</label>
                    <input className="input-text input-small" value={editingClinic.specialty} onChange={e => setEditingClinic({ ...editingClinic, specialty: e.target.value })} placeholder="例:腰痛、産後骨盤矯正" />
                  </div>
                  <div className="field">
                    <label className="field-lbl">スタッフの資格・経歴</label>
                    <input className="input-text input-small" value={editingClinic.staff} onChange={e => setEditingClinic({ ...editingClinic, staff: e.target.value })} placeholder="例:柔道整復師歴10年" />
                  </div>
                  <div className="field">
                    <label className="field-lbl">設備・機器</label>
                    <input className="input-text input-small" value={editingClinic.equipment} onChange={e => setEditingClinic({ ...editingClinic, equipment: e.target.value })} placeholder="例:超音波治療器、電気治療器" />
                  </div>
                </div>

                {error && <div className="error-box">{error}</div>}
                <div className="action-bar end">
                  <button className="btn btn-ghost" onClick={() => { setEditingClinicId(null); setEditingClinic(null); setError(''); }}>キャンセル</button>
                  <button className="btn btn-primary" onClick={async () => {
                    const ok = await saveClinic();
                    if (ok && editingClinicId === 'new') {
                      setEditingClinicId(null);
                      setEditingClinic(null);
                    }
                  }}>
                    {editingClinicId === 'new' ? '院を追加' : '変更を保存'}
                    <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                  </button>
                </div>
              </>
            )}

            {clinics.length === 0 && !editingClinic && (
              <div className="placeholder-card">
                <strong>まずは院情報を登録しましょう</strong>
                記事生成には院情報が必要です。下のボタンから最初の院を登録してください。
                <div style={{ marginTop: 16 }}>
                  <button className="btn btn-primary" onClick={() => startEditClinic('new')}>
                    最初の院を登録する
                    <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* STEP 1: KEYWORD */}
        {screen === 'keyword' && (
          <>
            <div className="page-head">
              <div>
                <div className="crumb">CREATE ARTICLE</div>
                <h1>新規記事を作成</h1>
                <p>キーワードと方向性を入力すると、最適なタイトル候補を生成します。</p>
              </div>
            </div>
            <StepBar current="keyword" />

            {activeClinic && (
              <div className="active-clinic-notice">
                <svg viewBox="0 0 24 24"><path d="M3 9 l9-6 9 6 v11 a2 2 0 0 1-2 2 H5 a2 2 0 0 1-2-2 z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                <div>
                  この記事は <strong>{activeClinic.clinic_name}</strong>({activeClinic.region} / {activeClinic.facility_type})向けに生成されます。
                  <span className="hint">サイドバーから別の院に切り替えできます。</span>
                </div>
              </div>
            )}

            <div className="form-block">
              <div className="form-head">
                <div className="num">01</div>
                <div>
                  <h3>メインキーワード <span className="badge-required">必須</span></h3>
                  <p>狙いたい検索キーワードを入力してください。「地域名+症状」または「症状+悩み」が効果的です。</p>
                </div>
              </div>
              <input className={`input-text ${keyword ? 'is-filled' : ''}`} value={keyword} onChange={e => setKeyword(e.target.value)} placeholder="例:肩こり 右側だけ" />
              <InstructionBox value={instructionStep1} onChange={setInstructionStep1} hint="例:産後ママをターゲットにして、専門用語は避けて優しい文体で書いてください。" />
            </div>

            {/* 02 CTAあり/なし(カード型2択) */}
            <div className="form-block">
              <div className="form-head">
                <div className="num">02</div>
                <div>
                  <h3>この記事で院への来院を促しますか? <span className="badge-required">必須</span></h3>
                  <p>記事のゴールを選んでください。見出しの作り方や文章のトーンが変わります。</p>
                </div>
              </div>
              <div className="radio-group">
                <label className={`radio-item ${ctaMode === 'with' ? 'is-selected' : ''}`}>
                  <input type="radio" name="ctaMode" value="with" checked={ctaMode === 'with'} onChange={e => setCtaMode(e.target.value)} style={{ display: 'none' }} />
                  <span className="radio-circle" />
                  <div className="info">
                    <div className="info-title">院へ誘導する</div>
                    <div className="info-desc">来院を促す集客記事。最後に院名入りのCTAを置き、院の強みや施術実績も反映します。</div>
                  </div>
                </label>
                <label className={`radio-item ${ctaMode === 'without' ? 'is-selected' : ''}`}>
                  <input type="radio" name="ctaMode" value="without" checked={ctaMode === 'without'} onChange={e => setCtaMode(e.target.value)} style={{ display: 'none' }} />
                  <span className="radio-circle" />
                  <div className="info">
                    <div className="info-title">誘導しない</div>
                    <div className="info-desc">院名やCTAを入れない情報記事。症状の原因やセルフケアを中立的に解説して締めます。</div>
                  </div>
                </label>
              </div>
            </div>

            {/* 03 参考文献(カード型2択) */}
            <div className="form-block">
              <div className="form-head">
                <div className="num">03</div>
                <div>
                  <h3>参考文献を入れますか?</h3>
                  <p>健康・医療の主張に出典を付けると信頼性が高まります。</p>
                </div>
              </div>
              <div className="radio-group">
                <label className={`radio-item ${refMode === 'none' ? 'is-selected' : ''}`}>
                  <input type="radio" name="refMode" value="none" checked={refMode === 'none'} onChange={e => setRefMode(e.target.value)} style={{ display: 'none' }} />
                  <span className="radio-circle" />
                  <div className="info">
                    <div className="info-title">入れない</div>
                    <div className="info-desc">参考文献なしで記事を生成します。</div>
                  </div>
                </label>
                <label className={`radio-item ${refMode === 'with' ? 'is-selected' : ''}`}>
                  <input type="radio" name="refMode" value="with" checked={refMode === 'with'} onChange={e => setRefMode(e.target.value)} style={{ display: 'none' }} />
                  <span className="radio-circle" />
                  <div className="info">
                    <div className="info-title">入れる</div>
                    <div className="info-desc">医学的な主張に参照番号(※1)を付けます。出典リンクの自動付与は準備中です。</div>
                  </div>
                </label>
              </div>
              {refMode === 'with' && (
                <div className="note-box warning" style={{ marginTop: 14 }}>
                  <div>現在は本文に参照番号(※1)を付けるところまで対応しています。出典元リンクの自動付与は準備中のため、公開前にご自身で出典を確認・追記してください。</div>
                </div>
              )}
            </div>

            {/* 04 事例(CTAありのときだけ表示) */}
            {ctaMode === 'with' && (
              <div className="form-block">
                <div className="form-head">
                  <div className="num">04</div>
                  <div>
                    <h3>患者さんの改善事例を入れますか?</h3>
                    <p>事例があると信頼性が増し、E-E-A-T評価にも効果的です。</p>
                  </div>
                </div>
                <div className="radio-group">
                  <label className={`radio-item ${caseMode === 'none' ? 'is-selected' : ''}`}>
                    <input type="radio" name="caseMode" value="none" checked={caseMode === 'none'} onChange={e => setCaseMode(e.target.value)} style={{ display: 'none' }} />
                    <span className="radio-circle" />
                    <div className="info">
                      <div className="info-title">入れない</div>
                      <div className="info-desc">事例セクションをスキップして本文のみ生成します。</div>
                    </div>
                  </label>
                  <label className={`radio-item ${caseMode === 'sample' ? 'is-selected' : ''}`}>
                    <input type="radio" name="caseMode" value="sample" checked={caseMode === 'sample'} onChange={e => setCaseMode(e.target.value)} style={{ display: 'none' }} />
                    <span className="radio-circle" />
                    <div className="info">
                      <div className="info-title">サンプルで生成</div>
                      <div className="info-desc">AIが架空のサンプル事例を生成します。公開前に必ず実際の事例に置き換えてください。</div>
                    </div>
                  </label>
                  <label className={`radio-item ${caseMode === 'real' ? 'is-selected' : ''}`}>
                    <input type="radio" name="caseMode" value="real" checked={caseMode === 'real'} onChange={e => setCaseMode(e.target.value)} style={{ display: 'none' }} />
                    <span className="radio-circle" />
                    <div className="info">
                      <div className="info-title">実際の事例情報を入力して生成</div>
                      <div className="info-desc">手元の改善事例を入力すると、それを記事に反映します。</div>
                    </div>
                    <span className="info-tag">推奨</span>
                  </label>
                </div>

                {caseMode === 'sample' && (
                  <div className="note-box warning" style={{ marginTop: 14 }}>
                    <div>AIがサンプル事例を生成します。必ず実際の事例に置き換えてからご利用ください。</div>
                  </div>
                )}
                {caseMode === 'real' && (
                  <div style={{ marginTop: 14 }}>
                    <p style={{ fontSize: 12, color: 'var(--text-sub)', marginBottom: 10 }}>※入力情報をもとにAIが事例文を生成します。公開前に内容をご確認ください。</p>
                    {cases.map((c, idx) => (
                      <div key={idx} className="case-card">
                        {cases.length > 1 && (
                          <button className="del" onClick={() => setCases(cases.filter((_, i) => i !== idx))}>× 削除</button>
                        )}
                        <div className="case-num">CASE {idx + 1}</div>
                        {[
                          { k: 'region', l: '居住地域', p: '例:大阪市中央区', r: true },
                          { k: 'age', l: '年代・性別', p: '例:40代女性', r: true },
                          { k: 'job', l: '職業', p: '例:デスクワーク', r: false },
                          { k: 'symptom', l: '主訴・症状', p: '例:慢性的な肩こりと頭痛', r: true },
                          { k: 'concern', l: '困っていたこと', p: '例:仕事に集中できない', r: true },
                          { k: 'treatment', l: '施術内容', p: '例:手技療法、姿勢矯正', r: true },
                          { k: 'period', l: '通院期間・回数', p: '例:約2ヶ月、週1回×8回', r: true },
                          { k: 'result', l: '結果・変化', p: '例:肩こりが大幅軽減', r: true },
                          { k: 'voice', l: '患者の感想', p: '例:楽になって嬉しい', r: false },
                        ].map(f => (
                          <div key={f.k} className="field">
                            <label className="field-lbl">{f.l}{f.r && <span className="req">*</span>}</label>
                            <input className="input-text input-small" value={c[f.k]} onChange={e => { const nc = [...cases]; nc[idx][f.k] = e.target.value; setCases(nc); }} placeholder={f.p} />
                          </div>
                        ))}
                      </div>
                    ))}
                    {cases.length < 3 && (
                      <button className="add-case-btn" onClick={() => setCases([...cases, { region: '', age: '', job: '', symptom: '', concern: '', treatment: '', period: '', result: '', voice: '' }])}>+ 事例を追加</button>
                    )}
                    <p style={{ fontSize: 11, color: 'var(--text-mute)', marginTop: 8 }}>※事例は最大3つまで追加できます</p>
                  </div>
                )}
              </div>
            )}

            {error && <div className="error-box">{error}</div>}
            <div className="action-bar end">
              {loading ? <Spinner step={loadStep} /> : (
                <button className="btn btn-primary" onClick={genTitles}>
                  タイトル候補を生成する
                  <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </button>
              )}
            </div>
          </>
        )}

        {/* STEP 2: TITLE */}
        {screen === 'title' && (
          <>
            <div className="page-head">
              <div>
                <div className="crumb">STEP 02 / TITLE</div>
                <h1>タイトルの選択</h1>
                <p>3つの候補から選択するか、独自のタイトルを入力してください。</p>
              </div>
            </div>
            <StepBar current="title" />

            <div className="form-block">
              <div className="form-head">
                <div className="num">01</div>
                <div>
                  <h3>タイトル候補</h3>
                  <p>クリックして選択してください。</p>
                </div>
              </div>
              {titles.map((t, i) => (
                <button key={i} className={`option-btn ${selTitle === t && !custTitle ? 'is-active' : ''}`} onClick={() => { setSelTitle(t); setCustTitle(''); }}>{t}</button>
              ))}
            </div>

            <div className="form-block">
              <div className="form-head">
                <div className="num">02</div>
                <div>
                  <h3>独自のタイトルで進める</h3>
                  <p>候補が気に入らない場合は、自由に入力してください。</p>
                </div>
              </div>
              <input className={`input-text ${custTitle ? 'is-filled' : ''}`} value={custTitle} onChange={e => setCustTitle(e.target.value)} placeholder="修正したいタイトルを入力" />
              <InstructionBox value={instructionStep2} onChange={setInstructionStep2} hint="例:構成では患者の不安に寄り添う流れにしてください。" />
            </div>

            {error && <div className="error-box">{error}</div>}
            <div className="action-bar">
              <button className="btn btn-ghost" onClick={() => setScreen('keyword')}>
                <svg viewBox="0 0 24 24"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                戻る
              </button>
              {loading ? <Spinner step={loadStep} /> : (
                <button className="btn btn-primary btn-grow" onClick={genStructure}>
                  このタイトルで構成案を作成する
                  <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </button>
              )}
            </div>
          </>
        )}

        {/* STEP 3: STRUCTURE */}
        {screen === 'structure' && (
          <>
            <div className="page-head">
              <div>
                <div className="crumb">STEP 03 / STRUCTURE</div>
                <h1>構成案の確認・編集</h1>
                <p>必要に応じて見出しを編集できます。確定後に本文を生成します。</p>
              </div>
            </div>
            <StepBar current="structure" />

            <div className="structure-toolbar">
              <div className="info">
                セクション <strong>{structure.length}</strong>個 / H3合計 <strong>{structure.reduce((sum, s) => sum + (s.h3s ? s.h3s.length : 0), 0)}</strong>個
              </div>
              <button className={`edit-toggle ${editMode ? 'is-on' : ''}`} onClick={() => setEditMode(!editMode)}>
                {editMode ? '✓ 編集を完了' : '✎ 構成を編集する'}
              </button>
            </div>

            {editMode && (
              <div className="insert-h2-line is-always-visible">
                <button onClick={() => setStructure([{ h2: '新しいセクション', h3s: [] }, ...structure])}>+ H2をここに追加</button>
              </div>
            )}
            {structure.map((sec, si) => (
              <React.Fragment key={si}>
                <div className="structure-card">
                  <div className="structure-row">
                    <span className="h-badge h2">H2</span>
                    {editMode ? (
                      <input className="structure-input" value={sec.h2} onChange={e => { const s = [...structure]; s[si].h2 = e.target.value; setStructure(s); }} />
                    ) : (
                      <span className="h-text h2-text">{sec.h2}</span>
                    )}
                    {editMode && <button className="del-btn" onClick={() => setStructure(structure.filter((_, i) => i !== si))}>削除</button>}
                  </div>
                  {sec.h3s.map((h3, hi) => (
                    <div key={hi} className="structure-row h3-row">
                      <span className="h-badge h3">H3</span>
                      {editMode ? (
                        <input className="structure-input" value={h3} onChange={e => { const s = [...structure]; s[si].h3s[hi] = e.target.value; setStructure(s); }} />
                      ) : (
                        <span className="h-text">{h3}</span>
                      )}
                      {editMode && <button className="del-btn" onClick={() => { const s = [...structure]; s[si].h3s = s[si].h3s.filter((_, i) => i !== hi); setStructure(s); }}>削除</button>}
                    </div>
                  ))}
                  {editMode && (
                    <button className="add-h3-btn" onClick={() => { const s = [...structure]; s[si].h3s.push('新しい見出し'); setStructure(s); }}>+ H3を追加</button>
                  )}
                </div>
                {editMode && (
                  <div className={`insert-h2-line ${si === structure.length - 1 ? 'is-always-visible' : ''}`}>
                    <button onClick={() => {
                      const s = [...structure];
                      s.splice(si + 1, 0, { h2: '新しいセクション', h3s: [] });
                      setStructure(s);
                    }}>+ H2をここに追加</button>
                  </div>
                )}
              </React.Fragment>
            ))}

            <div style={{ marginTop: 16 }}>
              <InstructionBox value={instructionStep3} onChange={setInstructionStep3} hint="例:本文では具体例を多めに入れてください。" />
            </div>

            {error && <div className="error-box">{error}</div>}
            <div className="action-bar">
              <button className="btn btn-ghost" onClick={() => setScreen('title')}>
                <svg viewBox="0 0 24 24"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                戻る
              </button>
              {loading ? <Spinner step={loadStep} /> : (
                <button className="btn btn-primary btn-grow" onClick={genArticle}>
                  この構成で記事を生成する
                  <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </button>
              )}
            </div>
          </>
        )}

        {/* STEP 4: ARTICLE */}
        {screen === 'article' && (
          <>
            <div className="page-head">
              <div>
                <div className="crumb">STEP 04 / FINISH</div>
                <h1>記事が完成しました</h1>
                <p>HTML形式とプレーンテキスト形式、両方をワンクリックでコピーできます。</p>
              </div>
              <button className="btn btn-ghost" onClick={resetForNew}>
                <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                新しい記事
              </button>
            </div>
            <StepBar current="article" />

            <div className="tab-bar">
              <button className={`tab ${tab === 'html' ? 'is-active' : ''}`} onClick={() => setTab('html')}>
                <svg viewBox="0 0 24 24"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                HTMLコード
              </button>
              <button className={`tab ${tab === 'preview' ? 'is-active' : ''}`} onClick={() => setTab('preview')}>
                <svg viewBox="0 0 24 24"><path d="M1 12 s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8 z"/><circle cx="12" cy="12" r="3"/></svg>
                プレビュー(編集可)
              </button>
              <div className="spacer" />
              <div className="meta">
                文字数 <strong>{(editedArticle || '').replace(/<[^>]+>/g, '').length.toLocaleString()}</strong>
              </div>
            </div>

            {tab === 'html' ? (
              <textarea readOnly value={editedArticle} className="article-pane" />
            ) : (
              <div
                className="article-preview"
                contentEditable
                suppressContentEditableWarning
                onBlur={e => {
                  const html = e.currentTarget.innerHTML;
                  setEditedArticle(html);
                  updateLatestHistory(html);
                }}
                dangerouslySetInnerHTML={{ __html: editedArticle }}
              />
            )}

            <div className="note-box">
              <div>
                プレビュータブでは本文を直接編集できます。編集後は両方のコピーボタンに反映されます。
              </div>
            </div>

            <div className="copy-grid copy-grid-3">
              <CopyButtonLarge
                mode="html"
                text={editedArticle}
                title="HTMLとしてコピー"
                desc="タグ込みのHTMLをコピー。WordPressクラシックエディタ等に。"
              />
              <CopyButtonLarge
                mode="rich"
                html={editedArticle}
                text={editedArticle}
                title="装飾付きでコピー"
                desc="見出し・段落の装飾を保ったままコピー。WordPressビジュアルエディタ・Wordに最適。"
              />
              <CopyButtonLarge
                mode="text"
                text={editedArticle.replace(/<[^>]+>/g, '').replace(/\n{3,}/g, '\n\n').trim()}
                title="プレーンテキストとしてコピー"
                desc="タグなしの本文だけコピー。SNS・メモ用に。"
              />
            </div>

            <div className="media-section">
              <div className="media-head">
                <h3>他媒体用に展開する</h3>
                <span className="badge">SHORT FORM</span>
              </div>
              {loading ? <Spinner step={loadStep} /> : (
                <div className="media-grid">
                  <button className="media-btn" data-type="hpb" onClick={() => genMedia('hotpepper')}>
                    <span className="dot" />
                    <div>
                      <div className="nm">ホットペッパー用</div>
                      <div className="sub">媒体の文字数・装飾に最適化</div>
                    </div>
                  </button>
                  <button className="media-btn" data-type="gmb" onClick={() => genMedia('gmb')}>
                    <span className="dot" />
                    <div>
                      <div className="nm">Googleマイビジネス用</div>
                      <div className="sub">投稿フォーマットで生成</div>
                    </div>
                  </button>
                  <button className="media-btn" data-type="line" onClick={() => genMedia('line')}>
                    <span className="dot" />
                    <div>
                      <div className="nm">LINE配信用</div>
                      <div className="sub">配信向けの長さ・トーン</div>
                    </div>
                  </button>
                </div>
              )}
            </div>

            {[['hotpepper', 'ホットペッパー用', '#E04E4E'], ['gmb', 'Googleマイビジネス用', '#3B6BE0'], ['line', 'LINE配信用', '#20875E']].map(([type, label, color]) => media[type] && (
              <div key={type} className="media-output">
                <div className="head">
                  <span className="label" style={{ color }}>{label}</span>
                  <CopyButton text={media[type]} />
                </div>
                <textarea readOnly value={media[type]} />
              </div>
            ))}
          </>
        )}

        {/* HISTORY (履歴) */}
                {/* ANALYTICS (集客ダッシュボード) */}
        {screen === 'analytics' && (
          <>
            <div className="page-head">
              <div>
                <div className="crumb">ANALYTICS</div>
                <h1>集客ダッシュボード</h1>
                <p>検索からどれだけ人が来ているか、どのページが読まれているかを確認できます。</p>
              </div>
            </div>
            <Analytics userId={ownerId || user.id} />
          </>
        )}
        {screen === 'history' && (
          <>
            <div className="page-head">
              <div>
                <div className="crumb">HISTORY</div>
                <h1>履歴</h1>
                <p>生成した記事の履歴を確認できます(直近{HISTORY_LIMIT}件)。</p>
              </div>
            </div>

            {account && !HISTORY_ENABLED_PLANS.includes(account.plan) && (
              <div className="upgrade-card">
                <div className="upgrade-icon">
                  <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div className="upgrade-text">
                  <div className="upgrade-eyebrow">UPGRADE REQUIRED</div>
                  <h2>履歴機能はスタンダードプラン以上で利用できます</h2>
                  <p>記事を最大{HISTORY_LIMIT}件まで保存し、いつでも閲覧・コピー・削除が可能です。複数の院を運営するアカウントでは、院ごとに履歴を絞り込んで管理できます。</p>
                  <div className="upgrade-features">
                    <div className="feature"><span className="check">✓</span> 直近{HISTORY_LIMIT}件の記事を自動保存</div>
                    <div className="feature"><span className="check">✓</span> 院別フィルタで管理しやすい</div>
                    <div className="feature"><span className="check">✓</span> HTML/プレーンテキストの両方をコピー可能</div>
                  </div>
                  <div className="upgrade-note">現在のプラン:<strong>{PLAN_LABELS[account.plan]}</strong></div>
                </div>
              </div>
            )}

            {account && HISTORY_ENABLED_PLANS.includes(account.plan) && (
              <>
                {clinics.length > 1 && (
                  <div className="history-filter">
                    <button
                      className={`history-filter-btn ${historyFilter === 'all' ? 'is-active' : ''}`}
                      onClick={() => setHistoryFilter('all')}
                    >
                      すべて <span className="count">{history.length}</span>
                    </button>
                    {clinics.map(c => {
                      const count = history.filter(h => h.clinic_id === c.id).length;
                      return (
                        <button
                          key={c.id}
                          className={`history-filter-btn ${historyFilter === c.id ? 'is-active' : ''}`}
                          onClick={() => setHistoryFilter(c.id)}
                        >
                          {c.clinic_name} <span className="count">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {historyLoading ? (
                  <div className="spinner-wrap">
                    <div className="spinner-circle" />
                    <p className="spinner-msg">履歴を読み込んでいます...</p>
                  </div>
                ) : history.length === 0 ? (
                  <div className="placeholder-card">
                    <strong>まだ履歴がありません</strong>
                    記事を生成すると、ここに自動で履歴が保存されます。
                  </div>
                ) : (
                  <div className="history-list">
                    {history
                      .filter(h => historyFilter === 'all' || h.clinic_id === historyFilter)
                      .map(h => {
                        const clinic = clinics.find(c => c.id === h.clinic_id);
                        const date = new Date(h.created_at);
                        const dateStr = `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
                        return (
                          <div key={h.id} className="history-item" onClick={() => loadHistoryDetail(h.id)}>
                            <div className="history-item-main">
                              <div className="history-item-title">{h.title || '(タイトルなし)'}</div>
                              <div className="history-item-meta">
                                <span className="kw">{h.keyword}</span>
                                {clinic && <span className="clinic">{clinic.clinic_name}</span>}
                                <span className="date">{dateStr}</span>
                              </div>
                            </div>
                            <div className="history-item-actions">
                              <button className="card-btn" onClick={(e) => { e.stopPropagation(); loadHistoryDetail(h.id); }}>閲覧</button>
                              <button className="card-btn danger" onClick={(e) => { e.stopPropagation(); deleteHistory(h.id); }}>削除</button>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}

                {viewingArticle && (
                  <div className="history-modal-overlay" onClick={() => setViewingArticle(null)}>
                    <div className="history-modal" onClick={(e) => e.stopPropagation()}>
                      <div className="history-modal-head">
                        <div>
                          <div className="history-modal-title">{viewingArticle.title || '(タイトルなし)'}</div>
                          <div className="history-modal-meta">
                            <span>キーワード:{viewingArticle.keyword}</span>
                            <span>{new Date(viewingArticle.created_at).toLocaleString('ja-JP')}</span>
                          </div>
                        </div>
                        <button className="history-modal-close" onClick={() => setViewingArticle(null)}>
                          <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </div>

                      <div className="history-modal-body">
                        <div className="article-preview history-preview" dangerouslySetInnerHTML={{ __html: viewingArticle.article_html || '' }} />
                      </div>

                      <div className="history-modal-foot">
                        <div className="copy-grid copy-grid-3">
                          <CopyButtonLarge
                            mode="html"
                            text={viewingArticle.article_html || ''}
                            title="HTMLとしてコピー"
                            desc="タグ込みのHTMLをコピー。WordPressクラシックエディタ等に。"
                          />
                          <CopyButtonLarge
                            mode="rich"
                            html={viewingArticle.article_html || ''}
                            text={viewingArticle.article_html || ''}
                            title="装飾付きでコピー"
                            desc="見出し・段落の装飾を保ったままコピー。ビジュアルエディタ・Wordに最適。"
                          />
                          <CopyButtonLarge
                            mode="text"
                            text={(viewingArticle.article_html || '').replace(/<[^>]+>/g, '').replace(/\n{3,}/g, '\n\n').trim()}
                            title="プレーンテキストとしてコピー"
                            desc="タグなしの本文だけコピー。SNS・メモ用に。"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {screen === 'members' && !isSubAccount && (
          <>
            <div className="page-head">
              <div>
                <div className="crumb">MEMBERS</div>
                <h1>サブアカウント管理</h1>
                <p>サブアカウントを招待して、複数の人で同じ契約を使うことができます。本数や院情報、履歴は共有されます。</p>
              </div>
            </div>

            {account && !TEAM_ENABLED_PLANS.includes(account.plan) && (
              <div className="upgrade-card">
                <div className="upgrade-icon">
                  <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div className="upgrade-text">
                  <div className="upgrade-eyebrow">UPGRADE REQUIRED</div>
                  <h2>サブアカウント機能はスタンダードプラン以上で利用できます</h2>
                  <p>同じ契約で複数のスタッフがログインできます。本数・院情報・履歴はすべて共有されるため、チームで効率的にツールを活用できます。</p>
                  <div className="upgrade-features">
                    <div className="feature"><span className="check">✓</span> スタンダード:最大3アカウント</div>
                    <div className="feature"><span className="check">✓</span> プレミアム:最大5アカウント</div>
                    <div className="feature"><span className="check">✓</span> 招待コード方式で安全に招待</div>
                  </div>
                  <div className="upgrade-note">現在のプラン:<strong>{PLAN_LABELS[account.plan]}</strong></div>
                </div>
              </div>
            )}

            {account && TEAM_ENABLED_PLANS.includes(account.plan) && (
              <>
                <div className="form-block">
                  <div className="form-head">
                    <div className="num">01</div>
                    <div>
                      <h3>アカウント使用状況</h3>
                      <p>あなた(オーナー)を含めた合計アカウント数です。</p>
                    </div>
                  </div>
                  <div className="usage-grid">
                    <div className="usage-card">
                      <div className="top">
                        <div className="label">利用中アカウント</div>
                      </div>
                      <div className="num">{currentAccountCount}<em>/{account.accounts_limit === 9999 ? '∞' : account.accounts_limit}</em></div>
                      <div className="remaining">
                        <span>オーナー1 + サブ{subAccounts.length} + 招待中{invitations.length}</span>
                        <strong>残り {account.accounts_limit === 9999 ? '無制限' : (account.accounts_limit - currentAccountCount) + '枠'}</strong>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-block">
                  <div className="form-head">
                    <div className="num">02</div>
                    <div>
                      <h3>サブアカウントを招待</h3>
                      <p>招待コードを発行して、相手に伝えてください。有効期限は7日間です。</p>
                    </div>
                  </div>

                  <div className="action-bar" style={{ marginTop: 0, marginBottom: 16 }}>
                    <button className="btn btn-primary" onClick={createInvitation} disabled={!canInvite}>
                      + 新しい招待コードを発行
                      <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    </button>
                  </div>

                  {!canInvite && (
                    <div className="note-box">
                      <div>現在のプラン({PLAN_LABELS[account.plan]})でのアカウント上限({account.accounts_limit}件)に達しています。</div>
                    </div>
                  )}

                  {invitations.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <div className="section-head">
                        <h3 style={{ fontSize: 14 }}>発行済みの招待コード <span className="muted">({invitations.length}件)</span></h3>
                      </div>
                      <div className="invitation-list">
                        {invitations.map(inv => {
                          const expiresAt = new Date(inv.expires_at);
                          const now = new Date();
                          const daysLeft = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
                          return (
                            <div key={inv.id} className="invitation-card">
                              <div className="invitation-code-display">
                                <code>{inv.invitation_code}</code>
                                <span className="expires">有効期限: あと{daysLeft}日</span>
                              </div>
                              <div className="invitation-actions">
                                <button className="card-btn" onClick={() => copyInvitationCode(inv.invitation_code)}>コピー</button>
                                <button className="card-btn danger" onClick={() => deleteInvitation(inv.id)}>取り消し</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-block">
                  <div className="form-head">
                    <div className="num">03</div>
                    <div>
                      <h3>登録済みサブアカウント</h3>
                      <p>現在あなたのサブとして登録されているアカウント一覧です。</p>
                    </div>
                  </div>

                  {subAccounts.length === 0 ? (
                    <div className="placeholder-card">
                      <strong>まだサブアカウントがいません</strong>
                      上の「+ 新しい招待コードを発行」から招待コードを作成し、相手に渡してください。
                    </div>
                  ) : (
                    <div className="sub-account-list">
                      {subAccounts.map(sub => {
                        const date = new Date(sub.created_at);
                        const dateStr = `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
                        return (
                          <div key={sub.id} className="sub-account-card">
                            <div className="sub-account-info">
                              <div className="sub-account-email">{sub.member_email}</div>
                              <div className="sub-account-meta">登録日: {dateStr}</div>
                            </div>
                            <div className="sub-account-actions">
                              <button className="card-btn danger" onClick={() => removeSubAccount(sub.id, sub.member_email)}>解除</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            {!isSubAccount && subAccounts.length === 0 && invitations.length === 0 && (
              <div className="form-block">
                <div className="form-head">
                  <div className="num">04</div>
                  <div>
                    <h3>招待コードを受け取った方へ</h3>
                    <p>他のオーナーから招待コードを受け取った場合は、ここに入力してください。サブアカウントとして相手のアカウントを利用できるようになります。</p>
                  </div>
                </div>
                <div className="field">
                  <label className="field-lbl">招待コード</label>
                  <input
                    className="input-text input-small"
                    value={inviteCodeInput}
                    onChange={e => setInviteCodeInput(e.target.value)}
                    placeholder="例:abc123-def456-ghi789"
                  />
                </div>
                {error && <div className="error-box">{error}</div>}
                <div className="action-bar end">
                  <button className="btn btn-primary" onClick={useInvitationCode}>
                    招待コードを使用する
                    <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                  </button>
                </div>
              </div>
            )}

            {error && <div className="error-box">{error}</div>}
          </>
        )}

        {screen === 'members' && isSubAccount && (
          <>
            <div className="page-head">
              <div>
                <div className="crumb">MEMBERS</div>
                <h1>サブアカウント</h1>
                <p>あなたはサブアカウントとしてログインしています。</p>
              </div>
            </div>
            <div className="placeholder-card">
              <strong>サブアカウントとしてログイン中</strong>
              オーナー: {account?.owner_email}<br/>
              本数・院情報・履歴はすべてオーナーと共有されています。<br/>
              サブアカウントの解除はオーナーに依頼してください。
            </div>
          </>
        )}

        {/* ADMIN(developerプラン専用) */}
        {screen === 'admin' && account && account.plan === 'developer' && (
          <>
            <div className="page-head">
              <div>
                <div className="crumb">ADMIN PANEL</div>
                <h1>契約者管理</h1>
                <p>登録ユーザーの一覧・プラン変更・使用量の調整ができます。</p>
              </div>
              <button className="btn btn-ghost" onClick={loadAdminUsers}>
                <svg viewBox="0 0 24 24"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9 a9 9 0 0 1 14.85-3.36 L23 10 M1 14 l4.64 4.36 A9 9 0 0 0 20.49 15"/></svg>
                更新
              </button>
            </div>

            <div className="admin-summary">
              <div className="admin-summary-card">
                <div className="label">総ユーザー数</div>
                <div className="num">{adminUsers.length}</div>
              </div>
              {['developer', 'premium', 'standard', 'light', 'free'].map(p => {
                const count = adminUsers.filter(u => u.plan === p).length;
                return (
                  <div key={p} className="admin-summary-card">
                    <div className="label">{PLAN_LABELS[p]}</div>
                    <div className="num">{count}<em>人</em></div>
                  </div>
                );
              })}
              <div className="admin-summary-card">
                <div className="label">今月の総記事数</div>
                <div className="num">{adminUsers.reduce((sum, u) => sum + (u.articles_used_this_month || 0), 0)}<em>本</em></div>
              </div>
            </div>

            <div className="admin-search">
              <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input
                type="text"
                value={adminSearch}
                onChange={e => setAdminSearch(e.target.value)}
                placeholder="メールアドレスで検索"
              />
            </div>

            {adminLoading ? (
              <div className="spinner-wrap">
                <div className="spinner-circle" />
                <p className="spinner-msg">ユーザー情報を読み込んでいます...</p>
              </div>
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>メール</th>
                      <th>プラン</th>
                      <th>院数</th>
                      <th>記事(今月)</th>
                      <th>構成(今月)</th>
                      <th>登録日</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminUsers
                      .filter(u => !adminSearch || u.email?.toLowerCase().includes(adminSearch.toLowerCase()))
                      .map(u => {
                        const limits = PLAN_LIMITS[u.plan] || PLAN_LIMITS.free;
                        const articlesLimit = u.articles_limit_override ?? limits.articles;
                        const structuresLimit = u.structures_limit_override ?? limits.structures;
                        const clinicLimit = u.clinic_limit_override ?? u.clinic_limit ?? limits.clinics;
                        const dateStr = u.created_at ? new Date(u.created_at).toLocaleDateString('ja-JP') : '-';
                        const isMe = u.id === user.id;
                        return (
                          <tr key={u.id} className={isMe ? 'is-me' : ''}>
                            <td>
                              <div className="cell-email">
                                {u.email || '(メールなし)'}
                                {isMe && <span className="me-badge">YOU</span>}
                              </div>
                            </td>
                            <td>
                              <span className={`plan-badge plan-${u.plan}`}>{PLAN_LABELS[u.plan] || u.plan}</span>
                            </td>
                            <td>
                              <span className="usage-cell">{u.clinic_count}<em>/{clinicLimit === 9999 ? '∞' : clinicLimit}</em></span>
                              {u.clinic_limit_override !== null && <span className="override-tag">override</span>}
                            </td>
                            <td>
                              <span className="usage-cell">{u.articles_used_this_month || 0}<em>/{articlesLimit === 9999 ? '∞' : articlesLimit}</em></span>
                              {u.articles_limit_override !== null && <span className="override-tag">override</span>}
                            </td>
                            <td>
                              <span className="usage-cell">{u.structures_used_this_month || 0}<em>/{structuresLimit === 9999 ? '∞' : structuresLimit}</em></span>
                              {u.structures_limit_override !== null && <span className="override-tag">override</span>}
                            </td>
                            <td className="cell-date">{dateStr}</td>
                            <td>
                              <button className="card-btn" onClick={() => startEditUser(u.id)}>編集</button>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                {adminUsers.filter(u => !adminSearch || u.email?.toLowerCase().includes(adminSearch.toLowerCase())).length === 0 && (
                  <div className="admin-empty">該当するユーザーがいません</div>
                )}
              </div>
            )}

            {editingUserId && editingUserData && (
              <div className="history-modal-overlay" onClick={() => { setEditingUserId(null); setEditingUserData(null); }}>
                <div className="history-modal admin-edit-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="history-modal-head">
                    <div>
                      <div className="history-modal-title">ユーザー情報の編集</div>
                      <div className="history-modal-meta">
                        <span>{adminUsers.find(x => x.id === editingUserId)?.email}</span>
                      </div>
                    </div>
                    <button className="history-modal-close" onClick={() => { setEditingUserId(null); setEditingUserData(null); }}>
                      <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>

                  <div className="history-modal-body">
                    <div className="admin-edit-section">
                      <h4>プラン変更</h4>
                      <div className="plan-radio-group">
                        {Object.keys(PLAN_LABELS).map(p => (
                          <label key={p} className={`plan-radio ${editingUserData.plan === p ? 'is-selected' : ''}`}>
                            <input type="radio" name="plan" value={p} checked={editingUserData.plan === p} onChange={e => setEditingUserData({ ...editingUserData, plan: e.target.value })} style={{ display: 'none' }} />
                            <span className="plan-radio-name">{PLAN_LABELS[p]}</span>
                            <span className="plan-radio-detail">
                              記事{PLAN_LIMITS[p].articles === 9999 ? '∞' : PLAN_LIMITS[p].articles}/構成{PLAN_LIMITS[p].structures === 9999 ? '∞' : PLAN_LIMITS[p].structures}/院{PLAN_LIMITS[p].clinics}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="admin-edit-section">
                      <h4>今月の使用数(直接編集)</h4>
                      <p className="admin-edit-hint">数値を直接書き換えられます。サービスや訂正に使用してください。</p>
                      <div className="field-grid-2">
                        <div className="field">
                          <label className="field-lbl">記事 使用数</label>
                          <input
                            type="number"
                            className="input-text input-small"
                            value={editingUserData.articles_used_this_month}
                            onChange={e => setEditingUserData({ ...editingUserData, articles_used_this_month: e.target.value })}
                          />
                        </div>
                        <div className="field">
                          <label className="field-lbl">構成 使用数</label>
                          <input
                            type="number"
                            className="input-text input-small"
                            value={editingUserData.structures_used_this_month}
                            onChange={e => setEditingUserData({ ...editingUserData, structures_used_this_month: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="admin-edit-section">
                      <h4>上限の個別オーバーライド</h4>
                      <p className="admin-edit-hint">プランの上限を個別に上書きします。空欄ならプラン通り、数値を入れるとその値が優先されます。</p>
                      <div className="field-grid-2">
                        <div className="field">
                          <label className="field-lbl">記事 上限(空欄=プラン通り)</label>
                          <input
                            type="number"
                            className="input-text input-small"
                            value={editingUserData.articles_limit_override ?? ''}
                            onChange={e => setEditingUserData({ ...editingUserData, articles_limit_override: e.target.value })}
                            placeholder={`プラン値:${PLAN_LIMITS[editingUserData.plan]?.articles ?? '-'}`}
                          />
                        </div>
                        <div className="field">
                          <label className="field-lbl">構成 上限(空欄=プラン通り)</label>
                          <input
                            type="number"
                            className="input-text input-small"
                            value={editingUserData.structures_limit_override ?? ''}
                            onChange={e => setEditingUserData({ ...editingUserData, structures_limit_override: e.target.value })}
                            placeholder={`プラン値:${PLAN_LIMITS[editingUserData.plan]?.structures ?? '-'}`}
                          />
                        </div>
                      </div>
                      <div className="field">
                        <label className="field-lbl">院数 上限(空欄=プラン通り)</label>
                        <input
                          type="number"
                          className="input-text input-small"
                          value={editingUserData.clinic_limit_override ?? ''}
                          onChange={e => setEditingUserData({ ...editingUserData, clinic_limit_override: e.target.value })}
                          placeholder={`プラン値:${PLAN_LIMITS[editingUserData.plan]?.clinics ?? '-'}`}
                        />
                      </div>
                    </div>

                    {error && <div className="error-box">{error}</div>}
                  </div>

                  <div className="history-modal-foot">
                    <div className="action-bar end" style={{ marginTop: 0 }}>
                      <button className="btn btn-ghost" onClick={() => { setEditingUserId(null); setEditingUserData(null); setError(''); }}>キャンセル</button>
                      <button className="btn btn-primary" onClick={saveUserChanges}>
                        変更を保存
                        <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {screen === 'preferences' && (
          <>
            <div className="page-head">
              <div>
                <div className="crumb">PLAN</div>
                <h1>プラン・お支払い</h1>
                <p>プランの変更やアップグレードができます。</p>
              </div>
            </div>

            {isSubAccount ? (
              <div className="placeholder-card">
                <strong>サブアカウントはプラン変更できません</strong>
                プランの変更はオーナーにご依頼ください。
              </div>
            ) : (
              <>
                <div className="note-box" style={{ marginBottom: 16 }}>
                  <div>現在のプラン:<strong>{account ? PLAN_LABELS[account.plan] : '-'}</strong></div>
                </div>

                {error && <div className="error-box">{error}</div>}

                <div className="clinics-grid">
                  {[
                    { key: 'light', name: 'ライトプラン', price: '6,980' },
                    { key: 'standard', name: 'スタンダードプラン', price: '11,500' },
                    { key: 'premium', name: 'プレミアムプラン', price: '29,800' },
                  ].map(p => {
                    const isCurrent = account?.plan === p.key;
                    return (
                      <div key={p.key} className="clinic-card">
                        <div className="clinic-card-head">
                          <div className="clinic-card-info">
                            <div className="clinic-card-name">{p.name}</div>
                            <div className="clinic-card-meta">月額 ¥{p.price}（税込）</div>
                          </div>
                          {isCurrent && <span className="badge-active">利用中</span>}
                        </div>
                        <div className="clinic-card-actions">
                          <button
                            className="btn btn-primary"
                            disabled={isCurrent}
                            onClick={() => handleUpgrade(p.key)}
                          >
                            {isCurrent ? '利用中のプラン' : 'このプランにする'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}